// src/pages/student-portal/courses/CourseDetailPage.js
// Student Portal - Course Detail Page (Purple Theme #8b5cf6)
// Ported from customer-dashboard (Next.js/TypeScript/Tailwind)

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
    LinearProgress,
    Snackbar,
    Drawer,
    useMediaQuery,
    Tooltip,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Tabs,
    Tab,
    TextField,
    Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SchoolIcon from '@mui/icons-material/School';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ArticleIcon from '@mui/icons-material/Article';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LayersIcon from '@mui/icons-material/Layers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SearchIcon from '@mui/icons-material/Search';
import TimerIcon from '@mui/icons-material/Timer';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import CheckIcon from '@mui/icons-material/Check';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axiosInstance from '../../../utils/axiosInstance';
import CoursePurchaseModal from './CoursePurchaseModal';
import { aiInstructorPersistence, aiInstructorService, getScriptTypeLabel, convertMarkdownToHtml as mdToHtml } from './aiInstructorUtils';

// Constants
const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';
const ACCENT_LIGHT = '#a78bfa';
const ACCENT_DARK = '#7c3aed';
const ACCENT_DARKER = '#6d28d9';
const ACCENT_BG = '#f5f3ff';
const ACCENT_BG2 = '#ede9fe';
const SIDEBAR_WIDTH = 340;

// Utility Functions

const CURRENCY_SYMBOLS = { usd: '$', eur: '€', gbp: '£', jpy: '¥', cad: 'CA$', aud: 'A$', inr: '₹', brl: 'R$', krw: '₩', mxn: 'MX$' };
function getCurrencySymbol(code) {
    if (!code) return '$';
    if (code.length <= 1 || CURRENCY_SYMBOLS[code.toLowerCase()]) return CURRENCY_SYMBOLS[(code || '').toLowerCase()] || '$';
    return code.toUpperCase() + ' ';
}

function formatTime(seconds) {
    if (seconds == null || isNaN(seconds) || seconds < 0) return '0:00';
    const totalSec = Math.floor(seconds);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const sPad = s < 10 ? `0${s}` : `${s}`;
    if (h > 0) {
        const mPad = m < 10 ? `0${m}` : `${m}`;
        return `${h}:${mPad}:${sPad}`;
    }
    return `${m}:${sPad}`;
}

function formatFileSize(bytes) {
    if (bytes == null || isNaN(bytes) || bytes <= 0) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function convertMarkdownToHtml(markdown) {
    if (!markdown) return '';
    let html = markdown;
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
        const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `<pre><code class="language-${lang}">${escaped}</code></pre>`;
    });
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
    html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    html = html.replace(/^[	 ]*[-*+]\s+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');
    html = html.replace(/^[ \t]*\d+\.\s+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br/>');
    if (!html.startsWith('<')) { html = `<p>${html}</p>`; }
    return html;
}

// Video URL Helpers

function detectVideoProvider(url) {
    if (!url) return 'unknown';
    const u = url.toLowerCase();
    if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
    if (u.includes('vimeo.com')) return 'vimeo';
    if (u.includes('dailymotion.com') || u.includes('dai.ly')) return 'dailymotion';
    if (u.includes('wistia.com') || u.includes('wistia.net')) return 'wistia';
    if (u.includes('loom.com')) return 'loom';
    return 'unknown';
}

function extractVideoId(url, provider) {
    if (!url) return null;
    try {
        const urlObj = new URL(url);
        switch (provider) {
            case 'youtube': {
                if (urlObj.hostname === 'youtu.be') return urlObj.pathname.slice(1);
                const v = urlObj.searchParams.get('v');
                if (v) return v;
                const embedMatch = urlObj.pathname.match(/\/embed\/([^/?]+)/);
                if (embedMatch) return embedMatch[1];
                return null;
            }
            case 'vimeo': {
                const vimeoMatch = urlObj.pathname.match(/\/(\d+)/);
                return vimeoMatch ? vimeoMatch[1] : null;
            }
            case 'dailymotion': {
                if (urlObj.hostname.includes('dai.ly')) return urlObj.pathname.slice(1);
                const dmMatch = urlObj.pathname.match(/\/video\/([^/?]+)/);
                return dmMatch ? dmMatch[1] : null;
            }
            case 'wistia': {
                const wistiaMatch = urlObj.pathname.match(/\/medias\/([^/?]+)/);
                return wistiaMatch ? wistiaMatch[1] : null;
            }
            case 'loom': {
                const loomMatch = urlObj.pathname.match(/\/share\/([^/?]+)/);
                return loomMatch ? loomMatch[1] : null;
            }
            default:
                return null;
        }
    } catch {
        return null;
    }
}

function generateEmbedUrl(url) {
    if (!url) return null;
    const provider = detectVideoProvider(url);
    const videoId = extractVideoId(url, provider);
    if (!videoId) return null;
    switch (provider) {
        case 'youtube':
            return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
        case 'vimeo':
            return `https://player.vimeo.com/video/${videoId}`;
        case 'dailymotion':
            return `https://www.dailymotion.com/embed/video/${videoId}`;
        case 'wistia':
            return `https://fast.wistia.net/embed/iframe/${videoId}`;
        case 'loom':
            return `https://www.loom.com/embed/${videoId}`;
        default:
            return null;
    }
}

function parseVideoUrl(url) {
    if (!url) return { provider: 'unknown', embedUrl: null, directUrl: url };
    const provider = detectVideoProvider(url);
    const embedUrl = generateEmbedUrl(url);
    return { provider, embedUrl, directUrl: url };
}

// Common Styles
const viewerHeaderSx = {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2,
    bgcolor: '#0f172a',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
};

const iconBadgeSx = {
    width: 36,
    height: 36,
    borderRadius: '12px',
    background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 4px 12px ${ACCENT}33`,
};

// ============================================================================
// CONTENT VIEWER COMPONENTS
// ============================================================================

const VideoPlayer = ({ content }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const videoUrl = content.content_data?.video_url || content.video_url || content.file_url;

    const togglePlay = () => {
        if (!videoRef.current) return;
        if (isPlaying) videoRef.current.pause();
        else videoRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => { if (videoRef.current) setCurrentTime(videoRef.current.currentTime); };
    const handleLoadedMetadata = () => { if (videoRef.current) setDuration(videoRef.current.duration); };
    const handleSeek = (e) => {
        if (!videoRef.current || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        videoRef.current.currentTime = pct * duration;
    };
    const toggleMute = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };
    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
        else document.exitFullscreen?.();
    };

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    if (!videoUrl) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#0f172a', py: 8 }}>
                <PlayCircleFilledIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.5)' }}>No video URL available</Typography>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a', position: 'relative' }}>
            <Box sx={viewerHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={iconBadgeSx}><PlayCircleFilledIcon sx={{ color: 'white', fontSize: 18 }} /></Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: 'white', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{content.title}</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Video</Typography>
                    </Box>
                </Box>
                <IconButton onClick={toggleFullscreen} sx={{ color: '#94a3b8', '&:hover': { color: 'white' } }}>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer' }} onClick={togglePlay}>
                <video ref={videoRef} src={videoUrl} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                {!isPlaying && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.3)' }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: `${ACCENT}cc`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PlayArrowIcon sx={{ color: 'white', fontSize: 40 }} />
                        </Box>
                    </Box>
                )}
            </Box>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#0f172a', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Box sx={{ height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, cursor: 'pointer', mb: 1.5, position: 'relative' }} onClick={handleSeek}>
                    <Box sx={{ height: '100%', bgcolor: ACCENT, borderRadius: 2, width: `${duration ? (currentTime / duration) * 100 : 0}%`, transition: 'width 0.1s' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={togglePlay} size="small" sx={{ color: 'white' }}>
                            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                        </IconButton>
                        <IconButton onClick={toggleMute} size="small" sx={{ color: 'white' }}>
                            {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                        </IconButton>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>{formatTime(currentTime)} / {formatTime(duration)}</Typography>
                    </Box>
                    <IconButton onClick={toggleFullscreen} size="small" sx={{ color: 'white' }}>
                        {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

const EmbeddedVideoPlayer = ({ content, embedUrl }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
        else document.exitFullscreen?.();
    };
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);
    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a' }}>
            <Box sx={viewerHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={iconBadgeSx}><PlayCircleFilledIcon sx={{ color: 'white', fontSize: 18 }} /></Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: 'white', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{content.title}</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Video</Typography>
                    </Box>
                </Box>
                <IconButton onClick={toggleFullscreen} sx={{ color: '#94a3b8', '&:hover': { color: 'white' } }}>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ flex: 1 }}>
                <iframe src={embedUrl} title={content.title} style={{ width: '100%', height: '100%', border: 'none' }} allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
            </Box>
        </Box>
    );
};

const SmartVideoViewer = ({ content }) => {
    const videoUrl = content.content_data?.video_url || content.video_url || content.file_url;
    const parsed = parseVideoUrl(videoUrl);
    if (parsed.embedUrl) return <EmbeddedVideoPlayer content={content} embedUrl={parsed.embedUrl} />;
    return <VideoPlayer content={content} />;
};

const DocumentViewer = ({ content }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const fileUrl = content.content_data?.document_url || content.file_url;
    const viewerUrl = fileUrl ? `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true` : null;

    const handleRetry = () => { if (loadAttempt < 3) { setLoadAttempt(p => p + 1); setIsLoading(true); setHasError(false); } };
    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
        else document.exitFullscreen?.();
    };
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);
    useEffect(() => { if (loadAttempt > 0 && loadAttempt < 3) { const t = setTimeout(() => setIsLoading(true), 500); return () => clearTimeout(t); } }, [loadAttempt]);

    if (!fileUrl) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#0f172a', py: 8 }}>
                <DescriptionIcon sx={{ fontSize: 64, color: `${ACCENT}66`, mb: 2 }} />
                <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', mb: 1 }}>No Document Available</Typography>
                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>This content doesn't have a document attached.</Typography>
            </Box>
        );
    }

    // If content is HTML text (no file URL or content type is text)
    if (content.content_type === 'text' || content.content_type === 'article' || (!fileUrl && (content.content || content.html_content || content.body))) {
        const htmlContent = content.content || content.html_content || content.body || content.description || '';
        return (
            <Box sx={{ height: '100%', overflow: 'auto', p: { xs: 2, md: 4 }, bgcolor: 'white' }}>
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 3 }}>{content.title}</Typography>
                {htmlContent ? (
                    <Box sx={{ fontFamily, color: '#334155', lineHeight: 1.8, '& h1,& h2,& h3,& h4': { fontFamily, color: '#1e293b', fontWeight: 700 }, '& p': { mb: 2 }, '& ul,& ol': { pl: 3, mb: 2 }, '& a': { color: ACCENT }, '& img': { maxWidth: '100%', borderRadius: '12px' }, '& code': { bgcolor: '#f1f5f9', px: 0.8, py: 0.3, borderRadius: '6px', fontSize: '0.85rem' }, '& pre': { bgcolor: '#1e293b', color: '#e2e8f0', p: 2.5, borderRadius: '12px', overflow: 'auto', mb: 2 } }}
                        dangerouslySetInnerHTML={{ __html: htmlContent }} />
                ) : (
                    <Typography sx={{ fontFamily, color: '#94a3b8' }}>No content available.</Typography>
                )}
            </Box>
        );
    }

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a', position: 'relative' }}>
            <Box sx={viewerHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={iconBadgeSx}><DescriptionIcon sx={{ color: 'white', fontSize: 18 }} /></Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: 'white', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{content.title}</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Document</Typography>
                    </Box>
                </Box>
                <IconButton onClick={toggleFullscreen} sx={{ color: '#94a3b8', '&:hover': { color: 'white' } }}>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, position: 'relative' }}>
                {isLoading && !hasError && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#0f172a' }}>
                        <CircularProgress size={40} sx={{ color: ACCENT, mb: 2 }} />
                        <Typography sx={{ fontFamily, color: 'white', fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>Loading Document</Typography>
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>{loadAttempt > 0 ? `Retry ${loadAttempt}/3...` : 'Preparing...'}</Typography>
                    </Box>
                )}
                {hasError && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#0f172a' }}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
                        <Typography sx={{ fontFamily, color: 'white', fontWeight: 700, mb: 1 }}>Couldn't Load</Typography>
                        <Button onClick={handleRetry} variant="contained" startIcon={<RefreshIcon />}
                            sx={{ fontFamily, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, textTransform: 'none', borderRadius: '10px' }}>
                            Try Again
                        </Button>
                    </Box>
                )}
                <iframe key={`doc-${loadAttempt}`} src={viewerUrl} title={content.title}
                    style={{ width: '100%', height: '100%', border: 'none', opacity: isLoading || hasError ? 0 : 1, transition: 'opacity 0.5s' }}
                    onLoad={() => setIsLoading(false)} onError={() => { setIsLoading(false); setHasError(true); }} />
            </Box>
        </Box>
    );
};

const SlideViewer = ({ content }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const fileUrl = content.content_data?.slide_url || content.content_data?.presentation_url || content.file_url;

    const getViewerUrl = () => {
        if (!fileUrl) return null;
        if (fileUrl.includes('docs.google.com/presentation')) return fileUrl.replace(/\/edit.*$/, '/embed?start=false&loop=false&delayms=3000');
        return `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    };
    const viewerUrl = getViewerUrl();
    const handleRetry = () => { if (loadAttempt < 3) { setLoadAttempt(p => p + 1); setIsLoading(true); setHasError(false); } };
    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
        else document.exitFullscreen?.();
    };
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    if (!fileUrl) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#0f172a', py: 8 }}>
                <ArticleIcon sx={{ fontSize: 64, color: `${ACCENT}66`, mb: 2 }} />
                <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', mb: 1 }}>No Slides Available</Typography>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a', position: 'relative' }}>
            <Box sx={viewerHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={iconBadgeSx}><ArticleIcon sx={{ color: 'white', fontSize: 18 }} /></Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: 'white', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{content.title}</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Presentation</Typography>
                    </Box>
                </Box>
                <IconButton onClick={toggleFullscreen} sx={{ color: '#94a3b8', '&:hover': { color: 'white' } }}>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, position: 'relative' }}>
                {isLoading && !hasError && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#0f172a' }}>
                        <CircularProgress size={40} sx={{ color: ACCENT, mb: 2 }} />
                        <Typography sx={{ fontFamily, color: 'white', fontSize: '0.85rem', fontWeight: 600 }}>Loading Slides</Typography>
                    </Box>
                )}
                {hasError && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#0f172a' }}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
                        <Typography sx={{ fontFamily, color: 'white', fontWeight: 700, mb: 1 }}>Couldn't Load</Typography>
                        <Button onClick={handleRetry} variant="contained" startIcon={<RefreshIcon />}
                            sx={{ fontFamily, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, textTransform: 'none', borderRadius: '10px' }}>Try Again</Button>
                    </Box>
                )}
                <iframe key={`slide-${loadAttempt}`} src={viewerUrl} title={content.title}
                    style={{ width: '100%', height: '100%', border: 'none', opacity: isLoading || hasError ? 0 : 1, transition: 'opacity 0.5s' }}
                    allowFullScreen onLoad={() => setIsLoading(false)} onError={() => { setIsLoading(false); setHasError(true); }} />
            </Box>
        </Box>
    );
};

const ImageViewer = ({ content }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const imageUrl = content.content_data?.image_url || content.file_url;
    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
        else document.exitFullscreen?.();
    };
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    if (!imageUrl) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#0f172a', py: 8 }}>
                <ImageIcon sx={{ fontSize: 64, color: `${ACCENT}66`, mb: 2 }} />
                <Typography sx={{ fontFamily, fontWeight: 700, color: 'white' }}>No Image Available</Typography>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a', overflow: 'hidden' }}>
            <Box sx={viewerHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={iconBadgeSx}><ImageIcon sx={{ color: 'white', fontSize: 18 }} /></Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: 'white', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{content.title}</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Image</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton onClick={() => setIsZoomed(!isZoomed)} sx={{ color: '#94a3b8', '&:hover': { color: 'white' } }}>
                        {isZoomed ? <FullscreenExitIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={toggleFullscreen} sx={{ color: '#94a3b8', '&:hover': { color: 'white' } }}>
                        {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, overflow: 'auto', cursor: isZoomed ? 'zoom-out' : 'zoom-in' }} onClick={() => setIsZoomed(!isZoomed)}>
                {isLoading && (
                    <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress size={40} sx={{ color: ACCENT, mb: 2 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>Loading image...</Typography>
                    </Box>
                )}
                {hasError && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: '#ef4444', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>Failed to load image</Typography>
                    </Box>
                )}
                <img src={imageUrl} alt={content.title}
                    style={{ maxWidth: '100%', maxHeight: isFullscreen ? '85vh' : '60vh', objectFit: 'contain', borderRadius: 12, border: '1px solid #334155', transition: 'transform 0.3s', transform: isZoomed ? 'scale(1.5)' : 'scale(1)', opacity: isLoading ? 0 : 1, display: hasError ? 'none' : 'block' }}
                    onLoad={() => setIsLoading(false)} onError={() => { setIsLoading(false); setHasError(true); }} />
            </Box>
        </Box>
    );
};

const EmbedViewer = ({ content }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const embedCode = content.content_data?.embed_code || content.embed_code;
    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
        else document.exitFullscreen?.();
    };
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    if (!embedCode) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#0f172a', py: 8 }}>
                <CodeIcon sx={{ fontSize: 64, color: `${ACCENT}66`, mb: 2 }} />
                <Typography sx={{ fontFamily, fontWeight: 700, color: 'white' }}>No Embed Content</Typography>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f172a' }}>
            <Box sx={viewerHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={iconBadgeSx}><CodeIcon sx={{ color: 'white', fontSize: 18 }} /></Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: 'white', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{content.title}</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Embedded Content</Typography>
                    </Box>
                </Box>
                <IconButton onClick={toggleFullscreen} sx={{ color: '#94a3b8', '&:hover': { color: 'white' } }}>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'white', mt: '56px' }} dangerouslySetInnerHTML={{ __html: embedCode }} />
        </Box>
    );
};

// ============================================================================
// CURRICULUM PREVIEW (Non-enrolled course overview)
// ============================================================================

const CurriculumPreviewModule = ({ module, moduleIndex, defaultExpanded }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const totalLessons = module.submodules?.reduce((acc, s) => acc + (s.content_items?.length || 0), 0) || 0;
    const submoduleCount = module.submodules?.length || 0;

    return (
        <Box sx={{ bgcolor: 'white' }}>
            {/* Module Header */}
            <Box onClick={() => setIsExpanded(!isExpanded)}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5, p: 2, transition: 'all 0.2s', '&:hover': { bgcolor: `${ACCENT}06` } }}>
                <Box sx={{ flexShrink: 0, width: 32, height: 32, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>M{moduleIndex + 1}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: ACCENT }}>Module {moduleIndex + 1}</Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{module.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.3 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <LayersIcon sx={{ fontSize: 10 }} />{submoduleCount} sections
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <PlayCircleFilledIcon sx={{ fontSize: 10 }} />{totalLessons} lessons
                        </Typography>
                        {module.has_test && (
                            <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <GpsFixedIcon sx={{ fontSize: 10 }} />Quiz
                            </Typography>
                        )}
                    </Box>
                </Box>
                <ExpandMoreIcon sx={{ color: '#94a3b8', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none', fontSize: 20, flexShrink: 0 }} />
            </Box>

            {/* Expanded Submodules & Content */}
            {isExpanded && (
                <Box sx={{ borderTop: `1px solid ${ACCENT}15`, bgcolor: '#fafafa' }}>
                    {module.submodules?.map((sub, subIdx) => (
                        <Box key={sub.id} sx={{ borderTop: subIdx > 0 ? '1px solid #f1f5f9' : 'none' }}>
                            {/* Submodule Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3, pr: 2, py: 1.2, bgcolor: `${ACCENT}06` }}>
                                <Box sx={{ width: 22, height: 22, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontFamily, fontWeight: 700, bgcolor: 'white', color: '#64748b', border: '1px solid #e2e8f0' }}>
                                    S{subIdx + 1}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: ACCENT_DARK }}>
                                        Submodule {subIdx + 1}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 500, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {sub.title}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', flexShrink: 0 }}>
                                    {sub.content_items?.length || 0} items
                                </Typography>
                            </Box>

                            {/* Content Items */}
                            <Box sx={{ py: 0.5 }}>
                                {sub.content_items?.map((ci, ciIdx) => (
                                    <Box key={ci.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 5, pr: 2, py: 1, '&:hover': { bgcolor: 'white' } }}>
                                        <Box sx={{ flexShrink: 0, width: 22, height: 22, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontFamily, fontWeight: 700, bgcolor: 'white', color: '#94a3b8', border: '1px solid #e2e8f0' }}>
                                            C{ciIdx + 1}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8' }}>
                                                Content {ciIdx + 1} {ci.content_type ? `\u2022 ${ci.content_type}` : ''}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 400, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {ci.title}
                                            </Typography>
                                        </Box>
                                        {ci.video_duration > 0 && (
                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', flexShrink: 0 }}>
                                                {Math.ceil(ci.video_duration / 60)}m
                                            </Typography>
                                        )}
                                        <LockIcon sx={{ fontSize: 12, color: '#cbd5e1', flexShrink: 0 }} />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}

                    {/* Module Quiz */}
                    {module.has_test && (
                        <Box sx={{ borderTop: '1px solid #f1f5f9', p: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '10px', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                                <Box sx={{ flexShrink: 0, width: 32, height: 32, borderRadius: '10px', bgcolor: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <GpsFixedIcon sx={{ color: 'white', fontSize: 16 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, color: '#b45309' }}>Module Quiz</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#92400e' }}>Test your knowledge</Typography>
                                </Box>
                                <LockIcon sx={{ fontSize: 14, color: '#f59e0b', flexShrink: 0 }} />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

// ============================================================================
// AI INSTRUCTOR PRESENTATION VIEW
// ============================================================================

const SCRIPT_TYPE_ICONS = {
    course_welcome: <AutoAwesomeIcon sx={{ fontSize: 18, color: ACCENT_LIGHT }} />,
    course_intro: <SchoolIcon sx={{ fontSize: 18, color: ACCENT_LIGHT }} />,
    module_intro: <LayersIcon sx={{ fontSize: 18, color: ACCENT_LIGHT }} />,
    module_complete: <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />,
    test_intro: <QuizIcon sx={{ fontSize: 18, color: '#f59e0b' }} />,
    test_pass: <EmojiEventsIcon sx={{ fontSize: 18, color: '#10b981' }} />,
    test_fail: <RefreshIcon sx={{ fontSize: 18, color: '#f59e0b' }} />,
    course_complete: <WorkspacePremiumIcon sx={{ fontSize: 18, color: '#fbbf24' }} />,
};

const InstructorPresentationView = ({ instructorName, courseTitle, scriptText, scriptType, isLoading, onSkip }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [isTypingText, setIsTypingText] = useState(false);

    useEffect(() => {
        if (!scriptText) { setDisplayedText(''); return; }
        setIsTypingText(true);
        setDisplayedText('');
        let index = 0;
        const interval = setInterval(() => {
            if (index < scriptText.length) {
                setDisplayedText(scriptText.slice(0, index + 1));
                index++;
            } else {
                setIsTypingText(false);
                clearInterval(interval);
            }
        }, 22);
        return () => clearInterval(interval);
    }, [scriptText]);

    return (
        <Box sx={{
            position: 'fixed', inset: 0, zIndex: 1400,
            background: `linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4c1d95 100%)`,
            display: 'flex', flexDirection: 'column',
        }}>
            {/* Background effects */}
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                <Box sx={{ position: 'absolute', top: '20%', left: '20%', width: 400, height: 400, bgcolor: `${ACCENT}15`, borderRadius: '50%', filter: 'blur(100px)' }} />
                <Box sx={{ position: 'absolute', bottom: '20%', right: '20%', width: 300, height: 300, bgcolor: '#7c3aed15', borderRadius: '50%', filter: 'blur(80px)' }} />
            </Box>

            {/* Skip button */}
            <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 30 }}>
                <Button onClick={onSkip} variant="text"
                    endIcon={<NavigateNextIcon />}
                    sx={{
                        fontFamily, fontWeight: 600, textTransform: 'none', color: 'rgba(255,255,255,0.8)',
                        bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '12px', px: 2.5, py: 1,
                        border: '1px solid rgba(255,255,255,0.15)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' },
                    }}>
                    Skip
                </Button>
            </Box>

            {/* Main content */}
            <Box sx={{ position: 'relative', zIndex: 10, flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'center', p: { xs: 3, md: 6 }, gap: { xs: 4, md: 8 }, overflow: 'auto' }}>
                {/* Avatar section */}
                <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ position: 'relative', mb: 3 }}>
                        {/* Glow ring */}
                        <Box sx={{
                            position: 'absolute', inset: -6, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`,
                            opacity: 0.3, filter: 'blur(12px)',
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': { '0%, 100%': { opacity: 0.3 }, '50%': { opacity: 0.5 } },
                        }} />
                        {/* Border ring */}
                        <Box sx={{
                            position: 'absolute', inset: -3, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${ACCENT_LIGHT}, ${ACCENT_DARK})`,
                            p: '2px',
                        }}>
                            <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', bgcolor: '#1e1b4b' }} />
                        </Box>
                        {/* Avatar circle */}
                        <Box sx={{
                            position: 'relative', width: { xs: 120, md: 160 }, height: { xs: 120, md: 160 },
                            borderRadius: '50%', overflow: 'hidden',
                            bgcolor: '#312e81', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <SmartToyIcon sx={{ fontSize: { xs: 48, md: 64 }, color: ACCENT_LIGHT }} />
                            {isLoading && (
                                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(30,27,75,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress size={32} sx={{ color: ACCENT_LIGHT }} />
                                </Box>
                            )}
                        </Box>
                        {/* Speaking indicator */}
                        <Box sx={{
                            position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)',
                            display: 'flex', alignItems: 'center', gap: 0.3,
                            px: 1.5, py: 0.5, bgcolor: ACCENT, borderRadius: '100px',
                        }}>
                            {[0, 1, 2, 3].map((i) => (
                                <Box key={i} sx={{
                                    width: 2, bgcolor: 'white', borderRadius: 2,
                                    height: `${6 + Math.random() * 8}px`,
                                    animation: 'wave 0.4s ease-in-out infinite alternate',
                                    animationDelay: `${i * 0.1}s`,
                                    '@keyframes wave': { '0%': { height: 4 }, '100%': { height: 14 } },
                                }} />
                            ))}
                        </Box>
                    </Box>
                    {/* Instructor info */}
                    <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: { xs: '1rem', md: '1.1rem' }, mb: 0.3 }}>
                        {instructorName || 'AI Instructor'}
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: ACCENT_LIGHT, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AutoAwesomeIcon sx={{ fontSize: 13 }} /> AI Learning Companion
                    </Typography>
                </Box>

                {/* Message section */}
                <Box sx={{ flex: 1, maxWidth: 600, width: '100%' }}>
                    {/* Script type badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Chip
                            icon={SCRIPT_TYPE_ICONS[scriptType] || <AutoAwesomeIcon sx={{ fontSize: 16, color: ACCENT_LIGHT }} />}
                            label={getScriptTypeLabel(scriptType)}
                            size="small"
                            sx={{
                                fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                '& .MuiChip-icon': { ml: '8px' },
                            }}
                        />
                    </Box>

                    {/* Message card */}
                    <Box sx={{ position: 'relative' }}>
                        <Box sx={{
                            position: 'absolute', inset: -4,
                            background: `linear-gradient(135deg, ${ACCENT}33, ${ACCENT_DARK}33)`,
                            borderRadius: '20px', filter: 'blur(16px)',
                        }} />
                        <Paper elevation={0} sx={{
                            position: 'relative',
                            bgcolor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)',
                            borderRadius: '18px', border: '1px solid rgba(255,255,255,0.1)',
                            p: { xs: 3, md: 4 },
                        }}>
                            {/* Quote mark */}
                            <Typography sx={{
                                position: 'absolute', top: 8, left: 12,
                                fontSize: '3rem', color: `${ACCENT}33`, fontFamily: 'serif', lineHeight: 1, userSelect: 'none',
                            }}>"</Typography>

                            {/* Script text */}
                            <Box sx={{ pl: 2, minHeight: 80 }}>
                                <Typography sx={{
                                    fontFamily, fontSize: { xs: '0.9rem', md: '1rem' },
                                    color: 'rgba(255,255,255,0.9)', lineHeight: 1.7,
                                }}>
                                    {displayedText}
                                    {isTypingText && (
                                        <Box component="span" sx={{
                                            display: 'inline-block', width: 2, height: 16,
                                            bgcolor: ACCENT_LIGHT, ml: 0.5, verticalAlign: 'text-bottom',
                                            animation: 'blink 1s step-end infinite',
                                            '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
                                        }} />
                                    )}
                                </Typography>
                            </Box>

                            {/* Course info footer */}
                            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 36, height: 36, borderRadius: '10px',
                                    bgcolor: `${ACCENT}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <SchoolIcon sx={{ color: ACCENT_LIGHT, fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                                        Current Course
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                                        {courseTitle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>

                    {/* Bottom skip action */}
                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button onClick={onSkip} variant="text" startIcon={<NavigateNextIcon />}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem',
                                color: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(255,255,255,0.06)',
                                borderRadius: '12px', px: 2.5, py: 1, border: '1px solid rgba(255,255,255,0.08)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                            }}>
                            Continue to Course
                        </Button>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', display: { xs: 'none', sm: 'block' } }}>
                            Press to skip introduction
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// ============================================================================
// CHATBOT PANEL
// ============================================================================

const SUGGESTED_QUESTIONS = [
    'Explain the key concepts',
    'Give me a practice exercise',
    'Summarize this module',
    'What should I focus on?',
];

const ChatbotPanel = ({ isOpen, onClose, courseIdentity, courseTitle, chatbotId }) => {
    const storageKey = `chat_${courseIdentity || 'default'}`;
    const greetingMsg = { id: 'greeting', sender: 'ai', content: `Hi! I'm your AI learning assistant for "${courseTitle || 'this course'}". Ask me anything about the course material, request explanations, or get practice exercises!`, isGreeting: true };

    const [messages, setMessages] = useState(() => {
        try {
            const saved = localStorage.getItem(`chatbot_history_${storageKey}`);
            if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed) && parsed.length > 0) return parsed; }
        } catch {}
        return [greetingMsg];
    });
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const scrollRef = useRef(null);

    // Persist messages
    useEffect(() => {
        if (messages.length > 0) {
            try { localStorage.setItem(`chatbot_history_${storageKey}`, JSON.stringify(messages.slice(-100))); } catch {}
        }
    }, [messages, storageKey]);

    // Auto-scroll
    useEffect(() => { if (scrollRef.current) setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50); }, [messages, isTyping]);

    const handleSend = async (text) => {
        const msgText = (text || inputText || '').trim();
        if (!msgText || isTyping) return;
        const userMsg = { id: `user_${Date.now()}`, sender: 'user', content: msgText };
        setMessages(prev => [...prev, userMsg]);
        setInputText('');
        setIsTyping(true);
        try {
            const res = await axiosInstance.post('/api/lms/portal/chat', { message: msgText, course_identity: courseIdentity, chatbot_id: chatbotId });
            const aiContent = res.data?.response || res.data?.message || 'I apologize, I could not process that request.';
            setMessages(prev => [...prev, { id: `ai_${Date.now()}`, sender: 'ai', content: aiContent }]);
        } catch {
            setMessages(prev => [...prev, { id: `err_${Date.now()}`, sender: 'ai', content: 'Sorry, something went wrong. Please try again.' }]);
        } finally { setIsTyping(false); }
    };

    const handleCopy = (text, id) => {
        navigator.clipboard?.writeText(text).then(() => { setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); });
    };

    const handleClearChat = () => {
        setMessages([greetingMsg]);
        try { localStorage.removeItem(`chatbot_history_${storageKey}`); } catch {}
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

    const showSuggestions = messages.length <= 1 && !isTyping;

    if (!isOpen) return null;

    return (
        <>
            <Box onClick={onClose} sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.3)', zIndex: 1200, backdropFilter: 'blur(4px)' }} />
            <Box sx={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: { xs: '100%', sm: 400 }, maxWidth: 420, bgcolor: 'white', zIndex: 1300, boxShadow: '-8px 0 32px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', transform: isOpen ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.3s ease-out' }}>
                {/* Header */}
                <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #e2e8f0', background: `linear-gradient(135deg, #1e293b, ${ACCENT_DARKER})` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ ...iconBadgeSx, width: 40, height: 40 }}><SmartToyIcon sx={{ color: 'white', fontSize: 20 }} /></Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>AI Course Assistant</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: ACCENT_LIGHT, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 12 }} /> {isTyping ? 'Thinking...' : 'Ready to help'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Clear chat">
                                <IconButton onClick={handleClearChat} size="small" sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}><CloseIcon fontSize="small" /></IconButton>
                        </Box>
                    </Box>
                </Box>

                {/* Messages */}
                <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#f8fafc' }}>
                    {messages.map((msg) => (
                        <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                            <Box sx={{
                                maxWidth: '88%', px: 2, py: 1.5, position: 'relative',
                                borderRadius: msg.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                background: msg.sender === 'user' ? `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})` : 'white',
                                color: msg.sender === 'user' ? 'white' : '#334155',
                                boxShadow: msg.sender === 'ai' ? '0 1px 3px rgba(0,0,0,0.08)' : '0 2px 8px rgba(139,92,246,0.2)',
                                border: msg.sender === 'ai' ? '1px solid #e2e8f0' : 'none',
                            }}>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</Typography>
                            </Box>
                            {msg.sender === 'ai' && !msg.isGreeting && (
                                <Box sx={{ mt: 0.3, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => handleCopy(msg.content, msg.id)}
                                        sx={{ p: 0.3, color: copiedId === msg.id ? '#10b981' : '#94a3b8', '&:hover': { color: ACCENT } }}>
                                        {copiedId === msg.id ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                                    </IconButton>
                                    {copiedId === msg.id && <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#10b981' }}>Copied</Typography>}
                                </Box>
                            )}
                        </Box>
                    ))}
                    {isTyping && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Box sx={{ bgcolor: 'white', borderRadius: '16px 16px 16px 4px', px: 2, py: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    {[0, 1, 2].map(i => (
                                        <Box key={i} sx={{
                                            width: 7, height: 7, borderRadius: '50%', bgcolor: ACCENT_LIGHT,
                                            animation: 'bounce 1.4s infinite ease-in-out both',
                                            animationDelay: `${i * 0.16}s`,
                                            '@keyframes bounce': { '0%, 80%, 100%': { transform: 'scale(0)' }, '40%': { transform: 'scale(1)' } },
                                        }} />
                                    ))}
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>Thinking...</Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Suggested Questions */}
                    {showSuggestions && (
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                                Suggested questions
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                {SUGGESTED_QUESTIONS.map((q, i) => (
                                    <Box key={i} onClick={() => handleSend(q)}
                                        sx={{
                                            px: 1.5, py: 1, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: 'white',
                                            cursor: 'pointer', transition: 'all 0.2s',
                                            '&:hover': { borderColor: ACCENT_LIGHT, bgcolor: '#faf5ff', transform: 'translateX(4px)' },
                                        }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#475569' }}>{q}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Input Area */}
                <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: 'white' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, bgcolor: '#f1f5f9', borderRadius: '12px', px: 2, py: 1.2, border: '2px solid transparent', '&:focus-within': { borderColor: ACCENT_LIGHT, bgcolor: 'white' }, transition: 'all 0.2s' }}>
                            <input type="text" value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about this course..."
                                disabled={isTyping} style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontFamily, fontSize: '0.85rem', color: '#1e293b' }} />
                        </Box>
                        <IconButton onClick={() => handleSend()} disabled={!inputText.trim() || isTyping}
                            sx={{ bgcolor: inputText.trim() && !isTyping ? ACCENT : '#e2e8f0', color: inputText.trim() && !isTyping ? 'white' : '#94a3b8', '&:hover': { bgcolor: inputText.trim() ? ACCENT_DARK : '#e2e8f0' }, borderRadius: '12px', width: 44, height: 44, flexShrink: 0 }}>
                            {isTyping ? <CircularProgress size={20} sx={{ color: '#94a3b8' }} /> : <SendIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', mt: 0.8, textAlign: 'center' }}>
                        AI can make mistakes. Verify important information.
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

// ============================================================================
// KNOWLEDGE TEST VIEW
// ============================================================================

const KnowledgeTestView = ({ moduleId, test, onComplete, onBack, onNextModule, isLastModule, courseTitle, moduleTitle, showSnackbar }) => {
    const [testStatus, setTestStatus] = useState('loading');
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [previousAttempts, setPreviousAttempts] = useState([]);
    const [currentAttempt, setCurrentAttempt] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const submitRef = useRef(false);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                const res = await axiosInstance.get(`/api/lms/portal/modules/${moduleId}/test/attempts`);
                const attempts = res.data?.attempts || res.data?.data || [];
                setPreviousAttempts(attempts);
                const inProgress = attempts.find(a => a.status === 'in_progress');
                if (inProgress?.id) {
                    setCurrentAttempt(inProgress);
                    if (inProgress.questions) setQuestions(inProgress.questions);
                    setTestStatus('in_progress');
                    if (test.time_limit && inProgress.started_at) {
                        const elapsed = Math.floor((Date.now() - new Date(inProgress.started_at).getTime()) / 1000);
                        setTimeRemaining(Math.max(0, test.time_limit * 60 - elapsed));
                    }
                } else { setTestStatus('idle'); }
            } catch { setTestStatus('idle'); }
            finally { setIsLoading(false); }
        };
        init();
    }, [moduleId, test.time_limit]);

    useEffect(() => {
        if (testStatus !== 'in_progress' || timeRemaining === null || timeRemaining <= 0) return;
        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev && prev <= 1) { if (currentAttempt?.id && !submitRef.current) handleSubmitTest(); return 0; }
                return prev ? prev - 1 : null;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [testStatus, timeRemaining, currentAttempt?.id]);

    const handleStartTest = async () => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.post(`/api/lms/portal/modules/${moduleId}/test/start`);
            const data = res.data;
            if (data.success !== false && (data.attempt?.id || data.data?.id)) {
                const attempt = data.attempt || data.data;
                setCurrentAttempt(attempt);
                if (data.questions?.length || attempt.questions?.length) setQuestions(data.questions || attempt.questions);
                setTestStatus('in_progress');
                setCurrentQuestionIndex(0);
                setAnswers({});
                if (test.time_limit) setTimeRemaining(test.time_limit * 60);
                showSnackbar('Test started! Good luck!', 'success');
            } else { showSnackbar(data.error || 'Failed to start test', 'error'); }
        } catch { showSnackbar('Failed to start test', 'error'); }
        finally { setIsLoading(false); }
    };

    const handleSubmitTest = async () => {
        if (!currentAttempt?.id) return;
        setIsSubmitting(true);
        submitRef.current = true;
        try {
            const res = await axiosInstance.post(`/api/lms/portal/test-attempts/${currentAttempt.id}/submit`, { answers });
            const data = res.data;
            if (data.success !== false) {
                const attRes = await axiosInstance.get(`/api/lms/portal/modules/${moduleId}/test/attempts`);
                setPreviousAttempts(attRes.data?.attempts || attRes.data?.data || []);
                if (data.passed) {
                    showSnackbar(`Congratulations! You passed with ${data.percentage}%!`, 'success');
                    onComplete(true, data.percentage || 0);
                } else {
                    showSnackbar(`You scored ${data.percentage}%. Need ${test.passing_score}% to pass.`, 'warning');
                    setTestStatus('submitted');
                    onComplete(false, data.percentage || 0);
                }
            } else { showSnackbar(data.error || 'Failed to submit', 'error'); }
        } catch { showSnackbar('Failed to submit test', 'error'); }
        finally { setIsSubmitting(false); submitRef.current = false; }
    };

    const attemptsUsed = previousAttempts.filter(a => a.status === 'submitted' || a.status === 'completed').length;
    const attemptsRemaining = test.max_attempts === 0 ? Infinity : test.max_attempts - attemptsUsed;
    const hasPassed = previousAttempts.some(a => a.passed);
    const canRetake = !hasPassed && attemptsRemaining > 0;
    const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    // Loading
    if (isLoading && testStatus === 'loading') {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 3, background: `linear-gradient(135deg, #1e293b, ${ACCENT_DARKER})` }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>{courseTitle || 'Course'}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <GpsFixedIcon sx={{ color: ACCENT_LIGHT, fontSize: 16 }} />
                        <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.85rem' }}>Loading Assessment...</Typography>
                    </Box>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={48} sx={{ color: ACCENT, mb: 2 }} />
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>Preparing your assessment...</Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    // Idle / Submitted - Test Info Card
    if (testStatus === 'idle' || testStatus === 'submitted') {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 3, background: `linear-gradient(135deg, #1e293b, ${ACCENT_DARKER})` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>{courseTitle || 'Course'}</Typography>
                        <Button onClick={onBack} startIcon={<NavigateBeforeIcon />} sx={{ fontFamily, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', textTransform: 'none', borderRadius: '10px', fontSize: '0.8rem', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>Back</Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LayersIcon sx={{ color: ACCENT_LIGHT, fontSize: 16 }} />
                        <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem' }}>{moduleTitle || 'Module'}</Typography>
                        <NavigateNextIcon sx={{ color: '#475569', fontSize: 16 }} />
                        <GpsFixedIcon sx={{ color: ACCENT_LIGHT, fontSize: 16 }} />
                        <Typography sx={{ fontFamily, color: 'white', fontSize: '0.8rem', fontWeight: 600 }}>Assessment</Typography>
                    </Box>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f8fafc' }}>
                    <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 } }}>
                        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <Box sx={{ p: 4, textAlign: 'center', background: `linear-gradient(135deg, #1e293b, #0f172a)`, color: 'white', position: 'relative' }}>
                                <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 2, borderRadius: '18px', background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px ${ACCENT}44` }}>
                                    <GpsFixedIcon sx={{ fontSize: 36 }} />
                                </Box>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>{test.title}</Typography>
                                <Chip label="Module Assessment" size="small" sx={{ fontFamily, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.15)', fontWeight: 600, fontSize: '0.7rem' }} />
                                {hasPassed && <Chip icon={<CheckCircleIcon sx={{ color: '#34d399 !important' }} />} label="Passed" size="small" sx={{ fontFamily, bgcolor: 'rgba(16,185,129,0.2)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)', ml: 1, fontWeight: 700, fontSize: '0.7rem' }} />}
                            </Box>
                            <Box sx={{ p: { xs: 3, sm: 4 } }}>
                                {test.instructions && (
                                    <Alert severity="info" sx={{ mb: 3, borderRadius: '12px', fontFamily, '& .MuiAlert-message': { fontFamily, fontSize: '0.85rem' } }}>{test.instructions}</Alert>
                                )}
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                                    {[
                                        { icon: <GpsFixedIcon sx={{ color: ACCENT }} />, value: `${test.passing_score}%`, label: 'Pass Score', bg: ACCENT_BG },
                                        { icon: <HelpOutlineIcon sx={{ color: '#3b82f6' }} />, value: test.question_count, label: 'Questions', bg: '#eff6ff' },
                                        { icon: <TimerIcon sx={{ color: '#f59e0b' }} />, value: test.time_limit || '∞', label: test.time_limit ? 'Minutes' : 'No Limit', bg: '#fffbeb' },
                                        { icon: <ReplayIcon sx={{ color: ACCENT }} />, value: attemptsRemaining === Infinity ? '∞' : attemptsRemaining, label: 'Attempts Left', bg: ACCENT_BG },
                                    ].map((stat, i) => (
                                        <Paper key={i} elevation={0} sx={{ p: 2, textAlign: 'center', borderRadius: '14px', border: '1px solid #e2e8f0', bgcolor: stat.bg }}>
                                            <Box sx={{ width: 40, height: 40, mx: 'auto', mb: 1, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{stat.icon}</Box>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.5rem', color: '#1e293b' }}>{stat.value}</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>{stat.label}</Typography>
                                        </Paper>
                                    ))}
                                </Box>
                                {previousAttempts.filter(a => a.status === 'submitted' || a.status === 'completed').length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#475569', mb: 1.5 }}>Your Attempts</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 200, overflow: 'auto' }}>
                                            {previousAttempts.filter(a => a.status === 'submitted' || a.status === 'completed').map((att) => (
                                                <Paper key={att.id} elevation={0} sx={{ p: 2, borderRadius: '12px', border: `2px solid ${att.passed ? '#bbf7d0' : '#e2e8f0'}`, bgcolor: att.passed ? '#f0fdf4' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: att.passed ? '#dcfce7' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {att.passed ? <EmojiEventsIcon sx={{ color: '#16a34a' }} /> : <CancelIcon sx={{ color: '#94a3b8' }} />}
                                                        </Box>
                                                        <Box>
                                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>Attempt {att.attempt_number}</Typography>
                                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b' }}>{att.correct_answers || 0} of {att.total_questions} correct</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'right' }}>
                                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.2rem', color: att.passed ? '#16a34a' : '#64748b' }}>{att.percentage}%</Typography>
                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: att.passed ? '#16a34a' : '#94a3b8' }}>{att.passed ? 'Passed' : 'Not Passed'}</Typography>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {hasPassed ? (
                                    <Box>
                                        <Paper elevation={0} sx={{ p: 3, mb: 2, borderRadius: '16px', border: '2px solid #bbf7d0', bgcolor: '#f0fdf4', textAlign: 'center' }}>
                                            <EmojiEventsIcon sx={{ fontSize: 40, color: '#16a34a', mb: 1 }} />
                                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#166534' }}>Congratulations! You've passed!</Typography>
                                        </Paper>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button fullWidth onClick={onBack} variant="outlined" sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px', borderColor: '#e2e8f0', color: '#475569', py: 1.5 }}>Back to Content</Button>
                                            {onNextModule && <Button fullWidth onClick={onNextModule} variant="contained" endIcon={<NavigateNextIcon />} sx={{ fontFamily, fontWeight: 700, textTransform: 'none', borderRadius: '12px', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, py: 1.5, boxShadow: 'none' }}>Continue</Button>}
                                        </Box>
                                    </Box>
                                ) : canRetake ? (
                                    <Box>
                                        {testStatus === 'submitted' && (
                                            <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>Not quite there yet. Review and try again!</Alert>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button onClick={onBack} variant="outlined" sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px', borderColor: '#e2e8f0', color: '#475569', px: 3, py: 1.5 }}>Cancel</Button>
                                            <Button fullWidth onClick={handleStartTest} disabled={isLoading} variant="contained" startIcon={isLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
                                                sx={{ fontFamily, fontWeight: 700, textTransform: 'none', borderRadius: '12px', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, py: 1.5, boxShadow: 'none' }}>
                                                {previousAttempts.length > 0 ? 'Retake Assessment' : 'Start Assessment'}
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>No attempts remaining. Contact support if needed.</Alert>
                                        <Button fullWidth onClick={onBack} variant="outlined" sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px', py: 1.5 }}>Return to Course</Button>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Box>
        );
    }

    // In Progress - Quiz
    if (testStatus === 'in_progress') {
        const currentQuestion = questions[currentQuestionIndex];
        const answeredCount = Object.keys(answers).length;
        const isLastQuestion = currentQuestionIndex === questions.length - 1;
        const progressPct = ((currentQuestionIndex + 1) / questions.length) * 100;

        if (!questions.length) {
            return (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 3, background: `linear-gradient(135deg, #1e293b, ${ACCENT_DARKER})` }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: 'white' }}>{courseTitle}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>No Questions Found</Typography>
                        <Button onClick={onBack} variant="contained" sx={{ fontFamily, bgcolor: ACCENT, textTransform: 'none', borderRadius: '10px' }}>Go Back</Button>
                    </Box>
                </Box>
            );
        }

        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexShrink: 0, p: 2, background: `linear-gradient(135deg, #1e293b, ${ACCENT_DARKER})` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: '1rem' }}>{courseTitle}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {timeRemaining !== null && (
                                <Chip size="small" icon={<TimerIcon sx={{ color: timeRemaining < 60 ? '#fca5a5 !important' : 'white !important', fontSize: 16 }} />}
                                    label={fmtTime(timeRemaining)} sx={{ fontFamily, fontWeight: 700, bgcolor: timeRemaining < 60 ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)', color: timeRemaining < 60 ? '#fca5a5' : 'white', border: `1px solid ${timeRemaining < 60 ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.1)'}`, fontFamily: 'monospace' }} />
                            )}
                            <Button onClick={onBack} size="small" startIcon={<CloseIcon sx={{ fontSize: 16 }} />} sx={{ fontFamily, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', textTransform: 'none', borderRadius: '10px', fontSize: '0.75rem', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>Exit</Button>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8rem' }}>
                            <LayersIcon sx={{ color: ACCENT_LIGHT, fontSize: 14 }} />
                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.75rem' }}>{moduleTitle}</Typography>
                            <NavigateNextIcon sx={{ color: '#475569', fontSize: 14 }} />
                            <GpsFixedIcon sx={{ color: ACCENT_LIGHT, fontSize: 14 }} />
                            <Typography sx={{ fontFamily, color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>{test.title}</Typography>
                        </Box>
                        <Chip size="small" label={`${currentQuestionIndex + 1} / ${questions.length}`} sx={{ fontFamily, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '0.75rem' }} />
                    </Box>
                    <LinearProgress variant="determinate" value={progressPct} sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: ACCENT_LIGHT, borderRadius: 3 } }} />
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f8fafc' }}>
                    <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, sm: 3 }, py: 3 }}>
                        {currentQuestion && (
                            <Box>
                                <Chip size="small" label={`Question ${currentQuestionIndex + 1}`} sx={{ fontFamily, fontWeight: 700, bgcolor: ACCENT, color: 'white', mb: 1.5, fontSize: '0.7rem' }} />
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.6 }}>{currentQuestion.question_text}</Typography>
                                {currentQuestion.image_url && (
                                    <Box sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                        <img src={currentQuestion.image_url} alt="Question" style={{ width: '100%', maxHeight: 300, objectFit: 'contain', background: '#f8fafc' }} />
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {(currentQuestion.question_type === 'true_false'
                                        ? [{ id: 'true', text: 'True' }, { id: 'false', text: 'False' }]
                                        : currentQuestion.options || []
                                    ).map((option, idx) => {
                                        const isSelected = answers[currentQuestion.id] === option.id;
                                        return (
                                            <Paper key={option.id} elevation={0} onClick={() => setAnswers(prev => ({ ...prev, [currentQuestion.id]: option.id }))}
                                                sx={{ p: 2, borderRadius: '14px', border: `2px solid ${isSelected ? ACCENT : '#e2e8f0'}`, bgcolor: isSelected ? ACCENT_BG : 'white', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 2, '&:hover': { borderColor: isSelected ? ACCENT : ACCENT_LIGHT, bgcolor: isSelected ? ACCENT_BG : '#faf5ff' } }}>
                                                <Box sx={{ width: 32, height: 32, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily, fontWeight: 700, fontSize: '0.75rem', bgcolor: isSelected ? ACCENT : '#f1f5f9', color: isSelected ? 'white' : '#64748b', transition: 'all 0.2s' }}>
                                                    {String.fromCharCode(65 + idx)}
                                                </Box>
                                                <Typography sx={{ fontFamily, fontSize: '0.9rem', flex: 1, color: isSelected ? ACCENT_DARKER : '#475569', fontWeight: isSelected ? 600 : 400 }}>{option.text}</Typography>
                                                <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isSelected ? ACCENT : '#cbd5e1'}`, bgcolor: isSelected ? ACCENT : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                                                    {isSelected && <CheckIcon sx={{ color: 'white', fontSize: 14 }} />}
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Paper elevation={0} sx={{ flexShrink: 0, borderTop: '1px solid #e2e8f0', p: 2, bgcolor: 'white' }}>
                    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', gap: 0.5, mb: 1.5, pb: 1.5, borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                            {questions.map((q, idx) => (
                                <Box key={q.id} onClick={() => setCurrentQuestionIndex(idx)}
                                    sx={{ width: 24, height: 24, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', bgcolor: idx === currentQuestionIndex ? '#1e293b' : answers[q.id] ? ACCENT_BG2 : '#f1f5f9', color: idx === currentQuestionIndex ? 'white' : answers[q.id] ? ACCENT_DARK : '#64748b', transform: idx === currentQuestionIndex ? 'scale(1.1)' : 'scale(1)' }}>
                                    {idx + 1}
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button onClick={() => setCurrentQuestionIndex(p => Math.max(0, p - 1))} disabled={currentQuestionIndex === 0} startIcon={<NavigateBeforeIcon />}
                                sx={{ fontFamily, fontSize: '0.8rem', textTransform: 'none', color: '#475569' }}>Previous</Button>
                            {isLastQuestion ? (
                                <Button onClick={handleSubmitTest} disabled={isSubmitting || answeredCount < questions.length} variant="contained" startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
                                    sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '10px', boxShadow: 'none' }}>Submit</Button>
                            ) : (
                                <Button onClick={() => setCurrentQuestionIndex(p => Math.min(questions.length - 1, p + 1))} variant="contained" endIcon={<NavigateNextIcon />}
                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', bgcolor: '#1e293b', '&:hover': { bgcolor: '#334155' }, borderRadius: '10px', boxShadow: 'none' }}>Next</Button>
                            )}
                        </Box>
                        {isLastQuestion && answeredCount < questions.length && (
                            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '10px', fontFamily, fontSize: '0.75rem', py: 0 }}>
                                Answer all {questions.length - answeredCount} remaining question{questions.length - answeredCount > 1 ? 's' : ''} to submit
                            </Alert>
                        )}
                    </Box>
                </Paper>
            </Box>
        );
    }

    return null;
};

// ============================================================================
// CONTENT DISPLAY
// ============================================================================

const ContentDisplay = ({ content, onComplete, onNext, onPrevious, hasNext, hasPrevious, currentIndex, totalItems, isCompleted, isCourseCompleted, isReviewMode, onBackToComplete, courseTitle, moduleTitle, subModuleTitle }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [userNotes, setUserNotes] = useState('');

    const renderContent = () => {
        switch ((content.content_type || '').toLowerCase()) {
            case 'video': return <SmartVideoViewer content={content} />;
            case 'image': return <ImageViewer content={content} />;
            case 'slide': case 'presentation': return <SlideViewer content={content} />;
            case 'embed': return <EmbedViewer content={content} />;
            default: return <DocumentViewer content={content} />;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Header */}
            <Box sx={{ flexShrink: 0, background: `linear-gradient(135deg, #1e293b 0%, #1e293b 50%, ${ACCENT_DARKER} 100%)`, position: 'relative' }}>
                {isReviewMode && (
                    <Box sx={{ bgcolor: '#f59e0b', px: 2, py: 0.8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VisibilityIcon sx={{ color: 'white', fontSize: 16 }} />
                            <Typography sx={{ fontFamily, color: 'white', fontSize: '0.8rem', fontWeight: 600 }}>Review Mode</Typography>
                        </Box>
                        {onBackToComplete && (
                            <Button onClick={onBackToComplete} startIcon={<EmojiEventsIcon sx={{ fontSize: 14 }} />} size="small"
                                sx={{ fontFamily, color: 'white', bgcolor: 'rgba(255,255,255,0.2)', textTransform: 'none', borderRadius: '8px', fontSize: '0.7rem', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>Back to Certificate</Button>
                        )}
                    </Box>
                )}
                <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: { xs: '0.95rem', sm: '1.1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '50%' }}>{courseTitle || 'Course'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip size="small" label={`${currentIndex + 1} / ${totalItems}`} sx={{ fontFamily, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '0.7rem', display: { xs: 'none', sm: 'flex' } }} />
                            {isCompleted && <Chip size="small" icon={<CheckCircleIcon sx={{ color: `${ACCENT_LIGHT} !important`, fontSize: 14 }} />} label="Done" sx={{ fontFamily, fontWeight: 600, bgcolor: `${ACCENT}33`, color: ACCENT_LIGHT, fontSize: '0.7rem', display: { xs: 'none', sm: 'flex' } }} />}
                            <Box sx={{ display: 'flex', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <IconButton onClick={onPrevious} disabled={!hasPrevious} size="small" sx={{ color: hasPrevious ? 'white' : '#475569', borderRight: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px 0 0 10px' }}><NavigateBeforeIcon fontSize="small" /></IconButton>
                                <IconButton onClick={onNext} disabled={!hasNext} size="small" sx={{ color: hasNext ? 'white' : '#475569', borderRadius: '0 10px 10px 0' }}><NavigateNextIcon fontSize="small" /></IconButton>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <LayersIcon sx={{ color: ACCENT_LIGHT, fontSize: 14, flexShrink: 0 }} />
                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{moduleTitle || 'Module'}</Typography>
                            <NavigateNextIcon sx={{ color: '#475569', fontSize: 14, flexShrink: 0 }} />
                            <FolderOpenIcon sx={{ color: ACCENT_LIGHT, fontSize: 14, flexShrink: 0 }} />
                            <Typography sx={{ fontFamily, color: 'white', fontSize: '0.75rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{subModuleTitle || content.title}</Typography>
                        </Box>
                        {!isReviewMode && !isCompleted && !isCourseCompleted && (
                            <Button onClick={onComplete} variant="contained" startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />} size="small"
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '10px', fontSize: '0.8rem', boxShadow: `0 4px 12px ${ACCENT}33`, whiteSpace: 'nowrap' }}>
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Mark Complete</Box>
                                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Done</Box>
                            </Button>
                        )}
                        {isCompleted && (
                            <Chip size="small" icon={<CheckCircleIcon sx={{ color: `${ACCENT_LIGHT} !important`, fontSize: 14 }} />} label="Completed"
                                sx={{ fontFamily, fontWeight: 600, bgcolor: `${ACCENT}33`, color: ACCENT_LIGHT, fontSize: '0.75rem', border: `1px solid ${ACCENT}44` }} />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Content Viewer */}
            <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: '100%', height: '100%' }}>{renderContent()}</Box>
            </Box>

            {/* Bottom Tabs */}
            <Box sx={{ flexShrink: 0, height: 140, bgcolor: 'white', display: 'flex', flexDirection: 'column', borderTop: '1px solid #e2e8f0' }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} centered sx={{ minHeight: 40, '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.8rem', minHeight: 40, color: '#64748b', '&.Mui-selected': { color: ACCENT } }, '& .MuiTabs-indicator': { bgcolor: ACCENT } }}>
                    <Tab label="Overview" />
                    <Tab label="Notes" />
                    <Tab label="Downloads" />
                </Tabs>
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 1.5, display: 'flex', justifyContent: 'center' }}>
                    {activeTab === 0 && (
                        <Box sx={{ maxWidth: 640, width: '100%', textAlign: 'center' }}>
                            {content.description ? (
                                <Box sx={{ fontFamily, color: '#475569', fontSize: '0.85rem', lineHeight: 1.7, '& a': { color: ACCENT } }} dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(content.description) }} />
                            ) : (
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No description available for this lesson.</Typography>
                            )}
                        </Box>
                    )}
                    {activeTab === 1 && (
                        <Box sx={{ maxWidth: 640, width: '100%', textAlign: 'center' }}>
                            <textarea value={userNotes} onChange={(e) => setUserNotes(e.target.value)} placeholder="Take notes for this lesson..."
                                style={{ width: '100%', height: 72, padding: 8, fontSize: '0.85rem', fontFamily, border: `1px solid ${ACCENT_BG2}`, borderRadius: 12, resize: 'none', outline: 'none', background: 'white' }} />
                        </Box>
                    )}
                    {activeTab === 2 && (
                        <Box sx={{ maxWidth: 640, width: '100%', textAlign: 'center' }}>
                            <Box sx={{ width: 40, height: 40, mx: 'auto', mb: 1, borderRadius: '50%', bgcolor: ACCENT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FolderOpenIcon sx={{ color: ACCENT, fontSize: 20 }} />
                            </Box>
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>No downloadable resources available.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ============================================================================
// COURSE COMPLETE VIEW
// ============================================================================

const CourseCompleteView = ({ course, onBackToCourses, onReviewContent }) => {
    return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Paper elevation={0} sx={{ maxWidth: 520, width: '100%', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 200, background: 'linear-gradient(135deg, #fffbeb, #fff7ed, white)', zIndex: 0 }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, px: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3, position: 'relative' }}>
                        <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(180deg, white, #fffbeb)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '4px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <EmojiEventsIcon sx={{ fontSize: 40, color: '#f59e0b' }} />
                        </Box>
                        <Chip label="COMPLETED" size="small" sx={{ position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', fontFamily, fontWeight: 700, fontSize: '0.6rem', bgcolor: '#10b981', color: 'white', border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                    </Box>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.3rem', color: '#1e293b', mb: 0.5 }}>Congratulations!</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem', mb: 3, maxWidth: 400 }}>
                        You have successfully mastered <Box component="span" sx={{ fontWeight: 700, color: '#1e293b', bgcolor: '#fffbeb', px: 1, py: 0.3, borderRadius: '6px', border: '1px solid #fde68a' }}>{course?.title}</Box>
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, width: '100%', mb: 3 }}>
                        {[
                            { icon: <CheckCircleIcon sx={{ color: '#10b981' }} />, value: '100%', label: 'Complete', bg: '#ecfdf5' },
                            { icon: <LayersIcon sx={{ color: '#3b82f6' }} />, value: course?.modules?.length || 0, label: 'Modules', bg: '#eff6ff' },
                            { icon: <StarIcon sx={{ color: '#f59e0b' }} />, value: course?.enrollment?.points_earned || 0, label: 'Points', bg: '#fffbeb' },
                        ].map((stat, i) => (
                            <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', bgcolor: stat.bg, textAlign: 'center' }}>
                                <Box sx={{ width: 32, height: 32, mx: 'auto', mb: 0.5, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{stat.icon}</Box>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>{stat.value}</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>{stat.label}</Typography>
                            </Paper>
                        ))}
                    </Box>
                    {course?.is_certificate_enabled && (
                        <Paper elevation={0} sx={{ width: '100%', mb: 3, p: 2, borderRadius: '14px', bgcolor: '#1e293b', border: '1px solid #334155' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <WorkspacePremiumIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                                </Box>
                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: '0.8rem' }}>Certificate Unlocked</Typography>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem' }}>Ready in certificate section</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {onReviewContent && (
                            <Button fullWidth onClick={onReviewContent} variant="contained" startIcon={<VisibilityIcon />}
                                sx={{ fontFamily, fontWeight: 700, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '12px', py: 1.3, boxShadow: 'none' }}>
                                Review Course Content
                            </Button>
                        )}
                        <Button fullWidth onClick={onBackToCourses} variant="outlined" startIcon={<ArrowBackIcon />}
                            sx={{ fontFamily, fontWeight: 700, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569', borderRadius: '12px', py: 1.3, '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' } }}>
                            Back to My Courses
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function CourseDetailPage() {
    const { courseIdentity } = useParams();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:960px)');

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewMode, setViewMode] = useState('content'); // 'content' | 'test' | 'course-complete'
    const [activeModule, setActiveModule] = useState(null);
    const [activeSubmodule, setActiveSubmodule] = useState(null);
    const [activeContent, setActiveContent] = useState(null);
    const [expandedModules, setExpandedModules] = useState([]);
    const [completedContents, setCompletedContents] = useState(new Set());
    const [completedModules, setCompletedModules] = useState(new Set());
    const [isCourseSidebarOpen, setIsCourseSidebarOpen] = useState(true);
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);
    const [activeTestModuleId, setActiveTestModuleId] = useState(null);
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [isEnrolling, setIsEnrolling] = useState(false);
    const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // AI Instructor state
    const [instructorActive, setInstructorActive] = useState(false);
    const [instructorLoading, setInstructorLoading] = useState(false);
    const [instructorScriptText, setInstructorScriptText] = useState('');
    const [instructorScriptType, setInstructorScriptType] = useState('');
    const previousModuleRef = useRef(null);
    const welcomeTriggeredRef = useRef(false);

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // Fetch course data
    const initialLoadRef = useRef(true);
    const fetchCourse = useCallback(async () => {
        const isInitial = initialLoadRef.current;
        if (isInitial) setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(`/api/lms/portal/courses/${courseIdentity}`);
            const data = res.data?.course || res.data?.data || res.data;
            setCourse(data);

            // Build completed sets from is_completed flags (injected by backend)
            // AND from progress_data arrays (fallback)
            const cContents = new Set();
            const cModules = new Set();
            const modules = data.modules || [];
            const progressData = data.enrollment?.progress_data || {};

            // From is_completed flags on each item
            modules.forEach(mod => {
                if (mod.is_completed) cModules.add(mod.id);
                (mod.submodules || []).forEach(sub => {
                    (sub.content_items || []).forEach(ci => {
                        if (ci.is_completed) cContents.add(ci.id);
                    });
                });
            });

            // Also from progress_data arrays (in case is_completed flags missing)
            (progressData.completed_contents || []).forEach(id => cContents.add(id));
            (progressData.completed_modules || []).forEach(id => {
                const numId = typeof id === 'string' ? parseInt(id, 10) : id;
                if (!isNaN(numId)) cModules.add(numId);
            });

            // If course is completed, mark everything as complete
            if (data.enrollment?.status === 'completed') {
                modules.forEach(mod => {
                    cModules.add(mod.id);
                    (mod.submodules || []).forEach(sub => {
                        (sub.content_items || []).forEach(ci => cContents.add(ci.id));
                    });
                });
            }

            // On initial load, replace sets; on background refresh, merge to avoid losing optimistic updates
            if (isInitial) {
                setCompletedContents(cContents);
                setCompletedModules(cModules);
            } else {
                setCompletedContents(prev => {
                    const merged = new Set(prev);
                    cContents.forEach(id => merged.add(id));
                    return merged;
                });
                setCompletedModules(prev => {
                    const merged = new Set(prev);
                    cModules.forEach(id => merged.add(id));
                    return merged;
                });
            }

            // Set first content active only on initial load
            if (isInitial && modules.length > 0) {
                initialLoadRef.current = false;
                // Resume from last position if available
                const lastPos = progressData.last_position;
                if (lastPos?.module_id && lastPos?.content_item_id) {
                    setExpandedModules([lastPos.module_id]);
                    setActiveModule(lastPos.module_id);
                    setActiveSubmodule(lastPos.submodule_id || null);
                    setActiveContent(lastPos.content_item_id);
                } else {
                    setExpandedModules([modules[0].id]);
                    const firstSub = modules[0].submodules?.[0];
                    const firstContent = firstSub?.content_items?.[0];
                    if (firstContent && firstSub) {
                        setActiveModule(modules[0].id);
                        setActiveSubmodule(firstSub.id);
                        setActiveContent(firstContent.id);
                    }
                }
            }

            // Check if course completed
            if (data.enrollment?.status === 'completed') {
                setViewMode('course-complete');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load course details.');
        } finally { if (initialLoadRef.current) { initialLoadRef.current = false; } setLoading(false); }
    }, [courseIdentity]);

    useEffect(() => { fetchCourse(); }, [fetchCourse]);

    // Navigation list - flat array of all content items
    const navigationList = useMemo(() => {
        if (!course?.modules) return [];
        const list = [];
        course.modules.forEach(mod => {
            (mod.submodules || []).forEach(sub => {
                (sub.content_items || []).forEach(ci => {
                    list.push({ moduleId: mod.id, submoduleId: sub.id, contentId: ci.id });
                });
            });
        });
        return list;
    }, [course]);

    const currentNavIndex = useMemo(() => navigationList.findIndex(n => n.contentId === activeContent), [navigationList, activeContent]);
    const hasNext = currentNavIndex < navigationList.length - 1;
    const hasPrevious = currentNavIndex > 0;

    const currentContent = useMemo(() => {
        if (!course?.modules || !activeContent) return null;
        for (const mod of course.modules) {
            for (const sub of (mod.submodules || [])) {
                const ci = (sub.content_items || []).find(c => c.id === activeContent);
                if (ci) return ci;
            }
        }
        return null;
    }, [course, activeContent]);

    const totalContentCount = navigationList.length;

    const getModuleContentStatus = useCallback((moduleId) => {
        if (!course?.modules) return { completedContents: 0, totalContents: 0, percentage: 0 };
        const mod = course.modules.find(m => m.id === moduleId);
        if (!mod) return { completedContents: 0, totalContents: 0, percentage: 0 };
        let total = 0, completed = 0;
        (mod.submodules || []).forEach(sub => {
            (sub.content_items || []).forEach(ci => {
                total++;
                if (completedContents.has(ci.id) || ci.is_completed) completed++;
            });
        });
        return { completedContents: completed, totalContents: total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    }, [course, completedContents]);

    const isAllModuleContentCompleted = useCallback((moduleId) => {
        const status = getModuleContentStatus(moduleId);
        return status.totalContents > 0 && status.completedContents >= status.totalContents;
    }, [getModuleContentStatus]);

    const isLastModule = useCallback((moduleId) => {
        if (!course?.modules?.length) return false;
        return course.modules[course.modules.length - 1].id === moduleId;
    }, [course]);

    const getModuleTest = useCallback((moduleId) => {
        if (!course?.modules) return null;
        const mod = course.modules.find(m => m.id === moduleId);
        return mod?.knowledge_test || null;
    }, [course]);

    const getNextModule = useCallback((currentModuleId) => {
        if (!course?.modules) return null;
        const idx = course.modules.findIndex(m => m.id === currentModuleId);
        return idx >= 0 && idx < course.modules.length - 1 ? course.modules[idx + 1] : null;
    }, [course]);

    const isLastContentInModule = useCallback((moduleId, contentId) => {
        if (!course?.modules) return false;
        const mod = course.modules.find(m => m.id === moduleId);
        if (!mod) return false;
        const allContent = [];
        (mod.submodules || []).forEach(sub => (sub.content_items || []).forEach(ci => allContent.push(ci.id)));
        return allContent.length > 0 && allContent[allContent.length - 1] === contentId;
    }, [course]);

    // Handlers
    const toggleModule = useCallback((moduleId) => {
        setExpandedModules(prev => prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]);
    }, []);

    const handleSelectContent = useCallback((moduleId, submoduleId, contentId) => {
        if (course?.enrollment?.status === 'completed' && !isReviewMode) return;
        setActiveModule(moduleId);
        setActiveSubmodule(submoduleId);
        setActiveContent(contentId);
        if (!isReviewMode) setViewMode('content');
        setActiveTestModuleId(null);
        if (!expandedModules.includes(moduleId)) setExpandedModules(prev => [...prev, moduleId]);
        // Track progress
        if (course?.enrollment?.status !== 'completed') {
            axiosInstance.post('/api/lms/portal/progress/track', { module_id: moduleId, submodule_id: submoduleId, content_id: contentId }).catch(() => {});
        }
    }, [course, isReviewMode, expandedModules]);

    // ── AI Instructor ──────────────────────────────────────────────
    const executeInstructorScript = useCallback(async (scriptType, context = {}) => {
        if (instructorActive) return; // Already showing
        setInstructorLoading(true);
        setInstructorScriptType(scriptType);
        setInstructorActive(true);

        const fullContext = {
            course_id: course?.id,
            courseTitle: course?.title,
            studentName: course?.enrollment?.student_name || 'Student',
            totalModules: course?.modules?.length || 1,
            ...context,
        };

        try {
            const result = await aiInstructorService.generateScript({
                chatbot_id: course?.chatbot_id,
                script_type: scriptType,
                context: fullContext,
                generate_audio: false,
            });
            if (result?.script_text) {
                setInstructorScriptText(result.script_text);
            } else {
                setInstructorScriptText(aiInstructorService.getDefaultScript(scriptType, fullContext));
            }
        } catch {
            setInstructorScriptText(aiInstructorService.getDefaultScript(scriptType, fullContext));
        } finally {
            setInstructorLoading(false);
        }

        aiInstructorService.logInteraction({
            chatbot_id: course?.chatbot_id,
            course_id: course?.id,
            script_type: scriptType,
            action: 'presented',
        });
    }, [instructorActive, course]);

    const handleSkipInstructor = useCallback(() => {
        aiInstructorService.logInteraction({
            chatbot_id: course?.chatbot_id,
            course_id: course?.id,
            script_type: instructorScriptType,
            action: 'skipped',
        });
        setInstructorActive(false);
        setInstructorScriptText('');
        setInstructorScriptType('');
    }, [course, instructorScriptType]);

    const handleMarkComplete = useCallback(async () => {
        if (!activeContent || !activeModule || !activeSubmodule) return;
        try {
            const contentRes = await axiosInstance.post(`/api/lms/portal/contents/${activeContent}/complete`);
            setCompletedContents(prev => new Set([...prev, activeContent]));

            // Update progress from backend response
            const contentData = contentRes.data?.data;
            if (contentData?.content_progress != null) {
                setCourse(prev => prev ? {
                    ...prev,
                    enrollment: { ...prev.enrollment, content_progress: contentData.content_progress }
                } : prev);
            }
            if (contentData?.progress_percentage != null) {
                setCourse(prev => prev ? {
                    ...prev,
                    enrollment: { ...prev.enrollment, progress_percentage: contentData.progress_percentage }
                } : prev);
            }

            showSnackbar('Lesson marked as complete!', 'success');
            const mod = course?.modules?.find(m => m.id === activeModule);

            // --- Backend cascading completion detected ---
            if (contentData?.course_completed) {
                if (mod) setCompletedModules(prev => new Set([...prev, mod.id]));
                await fetchCourse();
                setViewMode('course-complete');
                showSnackbar('Congratulations! Course completed!', 'success');
                executeInstructorScript('course_complete');
                return;
            }

            if (contentData?.module_completed) {
                if (mod) {
                    setCompletedModules(prev => new Set([...prev, mod.id]));
                    const modIndex = course?.modules?.findIndex(m => m.id === mod.id) ?? 0;
                    executeInstructorScript('module_complete', {
                        moduleTitle: mod.title,
                        moduleIndex: modIndex + 1,
                    });
                }
                // Navigate to next module
                const nextMod = getNextModule(activeModule);
                if (nextMod) {
                    setExpandedModules(prev => [...new Set([...prev, nextMod.id])]);
                    const firstSub = nextMod.submodules?.[0];
                    const firstContent = firstSub?.content_items?.[0];
                    if (firstContent && firstSub) {
                        setActiveModule(nextMod.id);
                        setActiveSubmodule(firstSub.id);
                        setActiveContent(firstContent.id);
                        showSnackbar('Moving to next module!', 'success');
                    }
                }
                fetchCourse();
                return;
            }

            // --- Frontend detection fallback ---
            if (!mod) return;
            let allComplete = true;
            (mod.submodules || []).forEach(sub => {
                (sub.content_items || []).forEach(ci => {
                    if (ci.id !== activeContent && !completedContents.has(ci.id) && !ci.is_completed) allComplete = false;
                });
            });

            if (allComplete && mod.has_test && mod.knowledge_test) {
                setActiveTestModuleId(mod.id);
                setViewMode('test');
                showSnackbar('Module complete! Starting Quiz...', 'info');
                return;
            }

            if (allComplete && !mod.has_test) {
                // Backend cascade should have handled this, but call sp_complete_module as backup
                let modData = null;
                try {
                    const modRes = await axiosInstance.post(`/api/lms/portal/modules/${mod.id}/complete`);
                    setCompletedModules(prev => new Set([...prev, mod.id]));
                    modData = modRes.data?.data;
                    if (modData?.progress_percentage != null) {
                        setCourse(prev => prev ? {
                            ...prev,
                            enrollment: { ...prev.enrollment, progress_percentage: modData.progress_percentage }
                        } : prev);
                    }
                } catch (modErr) {
                    console.warn('Module completion error:', modErr);
                }

                const modIndex = course?.modules?.findIndex(m => m.id === mod.id) ?? 0;
                executeInstructorScript('module_complete', {
                    moduleTitle: mod.title,
                    moduleIndex: modIndex + 1,
                });

                if (modData?.course_completed) {
                    await fetchCourse();
                    setViewMode('course-complete');
                    showSnackbar('Congratulations! Course completed!', 'success');
                    executeInstructorScript('course_complete');
                    return;
                }

                const nextMod = getNextModule(mod.id);
                if (nextMod) {
                    setExpandedModules(prev => [...new Set([...prev, nextMod.id])]);
                    const firstSub = nextMod.submodules?.[0];
                    const firstContent = firstSub?.content_items?.[0];
                    if (firstContent && firstSub) {
                        setActiveModule(nextMod.id);
                        setActiveSubmodule(firstSub.id);
                        setActiveContent(firstContent.id);
                        showSnackbar('Moving to next module!', 'success');
                    }
                }
                fetchCourse();
                return;
            }

            // Navigate to next content
            if (hasNext) {
                const nextItem = navigationList[currentNavIndex + 1];
                if (nextItem) handleSelectContent(nextItem.moduleId, nextItem.submoduleId, nextItem.contentId);
            }
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to mark as complete', 'error');
        }
    }, [activeContent, activeModule, activeSubmodule, course, completedContents, hasNext, navigationList, currentNavIndex, getNextModule, fetchCourse, handleSelectContent, showSnackbar, executeInstructorScript]);

    const handleNext = useCallback(() => {
        if (!hasNext) return;
        const nextItem = navigationList[currentNavIndex + 1];
        if (isReviewMode) {
            setActiveModule(nextItem.moduleId);
            setActiveSubmodule(nextItem.submoduleId);
            setActiveContent(nextItem.contentId);
            if (!expandedModules.includes(nextItem.moduleId)) setExpandedModules(prev => [...prev, nextItem.moduleId]);
        } else {
            handleSelectContent(nextItem.moduleId, nextItem.submoduleId, nextItem.contentId);
        }
    }, [hasNext, navigationList, currentNavIndex, isReviewMode, expandedModules, handleSelectContent]);

    const handlePrevious = useCallback(() => {
        if (!hasPrevious) return;
        const prevItem = navigationList[currentNavIndex - 1];
        if (isReviewMode) {
            setActiveModule(prevItem.moduleId);
            setActiveSubmodule(prevItem.submoduleId);
            setActiveContent(prevItem.contentId);
            if (!expandedModules.includes(prevItem.moduleId)) setExpandedModules(prev => [...prev, prevItem.moduleId]);
        } else {
            handleSelectContent(prevItem.moduleId, prevItem.submoduleId, prevItem.contentId);
        }
    }, [hasPrevious, navigationList, currentNavIndex, isReviewMode, expandedModules, handleSelectContent]);

    const handleStartTest = useCallback((moduleId) => {
        if (!isAllModuleContentCompleted(moduleId)) {
            const status = getModuleContentStatus(moduleId);
            showSnackbar(`Complete all lessons first (${status.completedContents}/${status.totalContents})`, 'warning');
            return;
        }
        // Trigger test intro AI instructor
        const mod = course?.modules?.find(m => m.id === moduleId);
        if (mod?.knowledge_test) {
            executeInstructorScript('test_intro', {
                testTitle: mod.knowledge_test.title || 'Assessment',
                questionCount: mod.knowledge_test.questions?.length || 10,
                moduleTitle: mod.title,
            });
        }
        setActiveTestModuleId(moduleId);
        setViewMode('test');
    }, [isAllModuleContentCompleted, getModuleContentStatus, showSnackbar, course, executeInstructorScript]);

    const handleTestComplete = useCallback(async (passed, score) => {
        const mod = course?.modules?.find(m => m.id === activeTestModuleId);
        const passingScore = mod?.knowledge_test?.passing_score || 70;

        // Trigger AI instructor for pass/fail
        if (passed) {
            executeInstructorScript('test_pass', { score, moduleTitle: mod?.title });
        } else {
            executeInstructorScript('test_fail', { score, passingScore, moduleTitle: mod?.title });
        }

        if (!passed || !activeTestModuleId) return;
        let modData = null;
        try {
            const modRes = await axiosInstance.post(`/api/lms/portal/modules/${activeTestModuleId}/complete`);
            setCompletedModules(prev => new Set([...prev, activeTestModuleId]));
            modData = modRes.data?.data;
            if (modData?.progress_percentage != null) {
                setCourse(prev => prev ? {
                    ...prev,
                    enrollment: { ...prev.enrollment, progress_percentage: modData.progress_percentage }
                } : prev);
            }
        } catch (modErr) {
            console.warn('Module completion error:', modErr);
        }

        // Check course completion from backend response
        if (modData?.course_completed) {
            await fetchCourse();
            setViewMode('course-complete');
            setActiveTestModuleId(null);
            showSnackbar('Congratulations! Course completed!', 'success');
            executeInstructorScript('course_complete');
        } else {
            // Trigger module complete instructor
            executeInstructorScript('module_complete', {
                moduleTitle: mod?.title,
                moduleIndex: (course?.modules?.findIndex(m => m.id === activeTestModuleId) ?? 0) + 1,
            });
            const nextMod = getNextModule(activeTestModuleId);
            if (nextMod) {
                setExpandedModules(prev => [...new Set([...prev, nextMod.id])]);
                const firstSub = nextMod.submodules?.[0];
                const firstContent = firstSub?.content_items?.[0];
                if (firstContent && firstSub) {
                    setActiveModule(nextMod.id);
                    setActiveSubmodule(firstSub.id);
                    setActiveContent(firstContent.id);
                    setViewMode('content');
                    showSnackbar('Moving to next module!', 'success');
                }
            }
            setActiveTestModuleId(null);
            fetchCourse();
        }
    }, [activeTestModuleId, getNextModule, fetchCourse, showSnackbar, course, executeInstructorScript]);

    const handleNextModuleFromTest = useCallback(() => {
        if (!activeTestModuleId) return;
        // Check if all modules are completed
        const totalModules = course?.modules?.length || 0;
        const allDone = completedModules.has(activeTestModuleId) && completedModules.size >= totalModules;
        if (allDone) {
            setViewMode('course-complete');
            setActiveTestModuleId(null);
            fetchCourse();
            return;
        }
        const nextMod = getNextModule(activeTestModuleId);
        if (nextMod) {
            setExpandedModules(prev => [...prev, nextMod.id]);
            const firstSub = nextMod.submodules?.[0];
            const firstContent = firstSub?.content_items?.[0];
            if (firstContent && firstSub) {
                setActiveModule(nextMod.id);
                setActiveSubmodule(firstSub.id);
                setActiveContent(firstContent.id);
                setViewMode('content');
            }
        }
        setActiveTestModuleId(null);
        fetchCourse();
    }, [activeTestModuleId, completedModules, course, getNextModule, fetchCourse]);

    const handleStartReview = useCallback(() => {
        setIsReviewMode(true);
        setViewMode('content');
        if (course?.modules?.[0]) {
            const m = course.modules[0];
            const s = m.submodules?.[0];
            const c = s?.content_items?.[0];
            if (c && s) {
                setActiveModule(m.id);
                setActiveSubmodule(s.id);
                setActiveContent(c.id);
                setExpandedModules(prev => [...new Set([...prev, m.id])]);
            }
        }
        showSnackbar('Review mode enabled!', 'info');
    }, [course, showSnackbar]);

    const handleBackToComplete = useCallback(() => {
        setIsReviewMode(false);
        setViewMode('course-complete');
    }, []);

    const handleEnroll = useCallback(async () => {
        // For paid courses, open purchase modal instead
        if (course?.is_paid && !course?.is_purchased) {
            setPurchaseModalOpen(true);
            return;
        }
        setIsEnrolling(true);
        try {
            await axiosInstance.post(`/api/lms/portal/courses/${courseIdentity}/enroll`);
            showSnackbar('Enrolled successfully!', 'success');
            fetchCourse();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to enroll', 'error');
        } finally { setIsEnrolling(false); }
    }, [courseIdentity, course, fetchCourse, showSnackbar]);

    const handlePurchaseSuccess = useCallback(() => {
        showSnackbar('Purchase successful! You are now enrolled.', 'success');
        setPurchaseModalOpen(false);
        fetchCourse();
    }, [fetchCourse, showSnackbar]);

    // Welcome trigger — fires once when course loads and student is enrolled
    useEffect(() => {
        if (!course?.id || !course?.enrollment || loading || welcomeTriggeredRef.current) return;
        if (course.enrollment.status === 'completed') return;
        const courseId = String(course.id);
        if (!aiInstructorPersistence.hasSeenWelcome(courseId)) {
            welcomeTriggeredRef.current = true;
            aiInstructorPersistence.markWelcomeSeen(courseId);
            executeInstructorScript('course_welcome');
        } else {
            welcomeTriggeredRef.current = true;
        }
    }, [course, loading, executeInstructorScript]);

    // Module intro trigger — fires when activeModule changes
    useEffect(() => {
        if (!course?.id || !activeModule || !course?.enrollment || instructorActive) return;
        if (course.enrollment.status === 'completed') return;
        if (previousModuleRef.current === activeModule) return;
        const prevMod = previousModuleRef.current;
        previousModuleRef.current = activeModule;
        if (!prevMod) return; // Skip first module set (welcome handles it)

        const courseId = String(course.id);
        const moduleId = String(activeModule);
        if (!aiInstructorPersistence.hasSeenModuleIntro(courseId, moduleId)) {
            aiInstructorPersistence.markModuleIntroSeen(courseId, moduleId);
            const mod = course.modules?.find(m => m.id === activeModule);
            const modIndex = course.modules?.findIndex(m => m.id === activeModule) ?? 0;
            executeInstructorScript('module_intro', {
                moduleTitle: mod?.title || 'this module',
                moduleIndex: modIndex + 1,
            });
        }
    }, [activeModule, course, instructorActive, executeInstructorScript]);

    const currentModuleTest = useMemo(() => activeTestModuleId ? getModuleTest(activeTestModuleId) : null, [activeTestModuleId, getModuleTest]);

    // Calculate real-time progress from completedContents (updates instantly on content completion)
    const overallProgress = useMemo(() => {
        if (!totalContentCount) return course?.enrollment?.progress_percentage || 0;
        const contentProgress = Math.round((completedContents.size / totalContentCount) * 100);
        return Math.max(contentProgress, course?.enrollment?.progress_percentage || 0);
    }, [completedContents.size, totalContentCount, course]);

    // Loading
    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: ACCENT, mb: 2 }} />
                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>Loading course...</Typography>
            </Box>
        );
    }

    // Error
    if (error) {
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily, mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={fetchCourse} sx={{ fontFamily, textTransform: 'none' }}>Retry</Button>}>
                    {error}
                </Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/student-portal/courses')} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Back to Courses</Button>
            </Box>
        );
    }

    if (!course) return null;

    // Not Enrolled
    if (!course.enrollment && !course.is_enrolled) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
                <Box sx={{ background: `linear-gradient(135deg, #1e293b 0%, #1e293b 40%, ${ACCENT_DARKER} 100%)`, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 } }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ fontFamily, color: '#cbd5e1', textTransform: 'none', mb: 3, fontSize: '0.85rem', '&:hover': { color: ACCENT_LIGHT } }}>Back to Courses</Button>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 6, alignItems: 'center' }}>
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {course.category && <Chip label={course.category} size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: `${ACCENT}33`, color: ACCENT_LIGHT, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1 }} />}
                                    {course.difficulty_level && <Chip label={course.difficulty_level} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', textTransform: 'capitalize', bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} />}
                                    {course.is_certificate_enabled && <Chip icon={<WorkspacePremiumIcon sx={{ color: '#fbbf24 !important', fontSize: 14 }} />} label="Certificate" size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontSize: '0.7rem' }} />}
                                </Box>
                                <Typography variant="h3" sx={{ fontFamily, fontWeight: 700, color: 'white', mb: 2, fontSize: { xs: '1.5rem', md: '2rem' }, lineHeight: 1.2 }}>{course.title}</Typography>
                                {course.short_description && <Typography sx={{ fontFamily, color: '#cbd5e1', mb: 3, lineHeight: 1.7, fontSize: '0.95rem' }}>{course.short_description}</Typography>}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                                    {course.estimated_duration_minutes && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#cbd5e1' }}>
                                            <AccessTimeIcon sx={{ fontSize: 18, color: ACCENT_LIGHT }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{Math.round(course.estimated_duration_minutes / 60)} hours</Typography>
                                        </Box>
                                    )}
                                    {course.modules?.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#cbd5e1' }}>
                                            <LayersIcon sx={{ fontSize: 18, color: ACCENT_LIGHT }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{course.modules.length} modules</Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Button onClick={handleEnroll} disabled={isEnrolling} variant="contained"
                                    startIcon={isEnrolling ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
                                    sx={{ fontFamily, fontWeight: 700, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '14px', px: 5, py: 1.5, fontSize: '0.95rem', boxShadow: `0 8px 24px ${ACCENT}44` }}>
                                    {isEnrolling ? 'Processing...' : course.is_paid ? 'Purchase Course' : 'Enroll for Free'}
                                </Button>
                            </Box>
                            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                                <Paper sx={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    {course.thumbnail_url ? (
                                        <img src={course.thumbnail_url} alt={course.title} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover' }} />
                                    ) : (
                                        <Box sx={{ width: '100%', aspectRatio: '16/9', background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <SchoolIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.8)' }} />
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {course.description && (
                    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 4 }, py: 4 }}>
                        <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ArticleIcon sx={{ color: 'white', fontSize: 16 }} /></Box>
                                About This Course
                            </Typography>
                            <Box sx={{ fontFamily, color: '#475569', lineHeight: 1.7, fontSize: '0.9rem', '& a': { color: ACCENT } }} dangerouslySetInnerHTML={{ __html: course.description }} />
                        </Paper>
                    </Box>
                )}

                {/* Course Curriculum */}
                {course.modules && course.modules.length > 0 && (
                    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 4 }, pb: 4 }}>
                        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', '&:hover': { borderColor: ACCENT + '44' }, transition: 'border-color 0.2s' }}>
                            <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', background: `linear-gradient(135deg, ${ACCENT_BG}, ${ACCENT_BG2})` }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LayersIcon sx={{ color: 'white', fontSize: 16 }} />
                                    </Box>
                                    Course Content
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mt: 1, ml: 5.5 }}>
                                    {course.modules.length} modules {'\u2022'} {course.modules.reduce((acc, m) => acc + (m.submodules?.reduce((subAcc, s) => subAcc + (s.content_items?.length || 0), 0) || 0), 0)} lessons
                                    {course.estimated_duration_minutes ? ` \u2022 ${Math.round(course.estimated_duration_minutes / 60)} hours total` : ''}
                                </Typography>
                            </Box>
                            <Box sx={{ '& > *:not(:last-child)': { borderBottom: '1px solid #f1f5f9' } }}>
                                {course.modules.map((mod, modIndex) => (
                                    <CurriculumPreviewModule key={mod.id} module={mod} moduleIndex={modIndex} defaultExpanded={modIndex === 0} />
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                )}

                {/* CTA Section */}
                <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 4 }, pb: 6 }}>
                    <Paper elevation={0} sx={{ background: `linear-gradient(135deg, #1e293b 0%, #1e293b 50%, ${ACCENT_DARKER} 100%)`, borderRadius: '20px', p: { xs: 4, sm: 6 }, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: -60, right: '25%', width: 200, height: 200, borderRadius: '50%', bgcolor: `${ACCENT}15` }} />
                        <Box sx={{ position: 'absolute', bottom: -40, left: '25%', width: 150, height: 150, borderRadius: '50%', bgcolor: `${ACCENT}10` }} />
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.3rem', color: 'white', mb: 1 }}>Ready to Start Learning?</Typography>
                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.9rem', mb: 3 }}>
                                {course.is_paid ? 'Purchase now and get instant access to all course content' : 'Enroll now and begin your journey to mastering new skills'}
                            </Typography>
                            {course.is_paid && (
                                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                                    {course.is_discount_active && course.discount_percentage > 0 ? (
                                        <>
                                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.8rem', color: ACCENT_LIGHT }}>
                                                {getCurrencySymbol(course.currency)}{Number(course.effective_price ?? course.price).toFixed(2)}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '1.1rem', color: '#94a3b8', textDecoration: 'line-through' }}>
                                                {getCurrencySymbol(course.currency)}{Number(course.price).toFixed(2)}
                                            </Typography>
                                            <Chip label={`-${course.discount_percentage}%`} size="small" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', bgcolor: '#ef4444', color: 'white' }} />
                                        </>
                                    ) : (
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.8rem', color: ACCENT_LIGHT }}>
                                            {getCurrencySymbol(course.currency)}{Number(course.effective_price || course.price || 0).toFixed(2)}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            <Button onClick={handleEnroll} disabled={isEnrolling} variant="contained"
                                startIcon={isEnrolling ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SchoolIcon />}
                                sx={{ fontFamily, fontWeight: 700, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '14px', px: 6, py: 1.5, fontSize: '1rem', boxShadow: `0 8px 24px ${ACCENT}44` }}>
                                {isEnrolling ? 'Processing...' : course.is_paid ? 'Purchase Course' : 'Enroll for Free'}
                            </Button>
                        </Box>
                    </Paper>
                </Box>

                {/* Purchase Modal */}
                <CoursePurchaseModal open={purchaseModalOpen} onClose={() => setPurchaseModalOpen(false)} course={course} onPurchaseSuccess={handlePurchaseSuccess} />
            </Box>
        );
    }

    // Main Enrolled View
    const currentModuleData = course.modules?.find(m => m.id === activeModule);
    const currentSubmoduleData = currentModuleData?.submodules?.find(s => s.id === activeSubmodule);

    return (
        <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
                {/* Left Sidebar */}
                {viewMode !== 'course-complete' && (
                    <Box sx={{ flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'white', borderRight: '1px solid #e2e8f0', transition: 'width 0.3s', width: isCourseSidebarOpen ? { xs: 280, sm: 320, lg: 360 } : 48 }}>
                        {!isCourseSidebarOpen ? (
                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                                <IconButton onClick={() => setIsCourseSidebarOpen(true)} sx={{ bgcolor: ACCENT, color: 'white', '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '12px', boxShadow: `0 4px 12px ${ACCENT}33` }}>
                                    <NavigateNextIcon />
                                </IconButton>
                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <ArticleIcon sx={{ color: ACCENT, fontSize: 18 }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.5rem', fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: 1, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Content</Typography>
                                </Box>
                                <Box sx={{ mt: 'auto', mb: 2 }}>
                                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: ACCENT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: ACCENT }}>{overallProgress}%</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                {/* Sidebar Header */}
                                <Box sx={{ flexShrink: 0, px: 2, py: 2, borderBottom: '1px solid #e2e8f0', background: `linear-gradient(135deg, #1e293b, ${ACCENT_DARKER})` }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ ...iconBadgeSx, width: 36, height: 36 }}><ArticleIcon sx={{ color: 'white', fontSize: 16 }} /></Box>
                                            <Box>
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: '0.85rem' }}>Course Content</Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: ACCENT_LIGHT, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AutoAwesomeIcon sx={{ fontSize: 12 }} /> {completedContents.size} of {totalContentCount} completed
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton onClick={() => setIsCourseSidebarOpen(false)} size="small" sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                            <NavigateBeforeIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    {course.enrollment && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8' }}>Progress</Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: 'white' }}>{overallProgress}%</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={overallProgress}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: ACCENT_LIGHT, borderRadius: 3 } }} />
                                        </Box>
                                    )}
                                </Box>

                                {/* Module List */}
                                <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: '#f8fafc' }}>
                                    {course.modules?.map((mod, modIndex) => {
                                        let isLockedByPrev = false;
                                        if (modIndex > 0) {
                                            const prevMod = course.modules[modIndex - 1];
                                            if (prevMod.has_test && !completedModules.has(prevMod.id) && !prevMod.is_completed) isLockedByPrev = true;
                                        }
                                        const isLocked = mod.is_locked || isLockedByPrev;
                                        const isModComplete = completedModules.has(mod.id) || mod.is_completed;
                                        const isActive = activeModule === mod.id;
                                        const isExpanded = expandedModules.includes(mod.id);
                                        const status = getModuleContentStatus(mod.id);

                                        return (
                                            <Paper key={mod.id} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', bgcolor: 'white' }}>
                                                <Box onClick={() => !isLocked && toggleModule(mod.id)}
                                                    sx={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.5 : 1, '&:hover': isLocked ? {} : { bgcolor: ACCENT_BG + '44' }, transition: 'all 0.2s' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                                                        <Box sx={{ width: 32, height: 32, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontFamily, fontWeight: 700, bgcolor: isLocked ? '#f1f5f9' : isModComplete ? '#10b981' : isActive ? ACCENT_BG : '#f1f5f9', color: isLocked ? '#94a3b8' : isModComplete ? 'white' : isActive ? ACCENT : '#64748b' }}>
                                                            {isLocked ? <LockIcon sx={{ fontSize: 14 }} /> : isModComplete ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : `M${modIndex + 1}`}
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: ACCENT }}>Module {modIndex + 1}</Typography>
                                                            <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mod.title}</Typography>
                                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.3 }}><LayersIcon sx={{ fontSize: 10 }} />{mod.submodules?.length || 0} sections</Typography>
                                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.3 }}><PlayCircleFilledIcon sx={{ fontSize: 10 }} />{status.totalContents} lessons</Typography>
                                                                {mod.has_test && <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 0.3 }}><GpsFixedIcon sx={{ fontSize: 10 }} />Quiz</Typography>}
                                                            </Box>
                                                        </Box>
                                                        {!isLocked && <ExpandMoreIcon sx={{ color: '#94a3b8', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none', fontSize: 20 }} />}
                                                    </Box>
                                                    {!isLocked && (
                                                        <Box sx={{ px: 1.5, pb: 1 }}>
                                                            <LinearProgress variant="determinate" value={status.percentage}
                                                                sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 2 } }} />
                                                        </Box>
                                                    )}
                                                </Box>

                                                {!isLocked && isExpanded && (
                                                    <Box sx={{ borderTop: '1px solid #f1f5f9', bgcolor: '#fafafa' }}>
                                                        {mod.submodules?.map((sub, subIdx) => {
                                                            const subComplete = (sub.content_items || []).every(ci => completedContents.has(ci.id) || ci.is_completed);
                                                            return (
                                                                <Box key={sub.id} sx={{ borderTop: subIdx > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                                                    <Box sx={{ pl: 2.5, pr: 1.5, py: 1, bgcolor: '#f1f5f9' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Box sx={{ width: 22, height: 22, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontFamily, fontWeight: 700, bgcolor: subComplete ? '#10b981' : '#e2e8f0', color: subComplete ? 'white' : '#64748b' }}>
                                                                                {subComplete ? <CheckIcon sx={{ fontSize: 12 }} /> : `S${subIdx + 1}`}
                                                                            </Box>
                                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: ACCENT_DARK }}>Section {subIdx + 1}</Typography>
                                                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 500, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.title}</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                    <Box sx={{ py: 0.5 }}>
                                                                        {(sub.content_items || []).map((ci, ciIdx) => {
                                                                            const isCiComplete = completedContents.has(ci.id) || ci.is_completed;
                                                                            const isCiActive = activeContent === ci.id;
                                                                            return (
                                                                                <Box key={ci.id} onClick={() => handleSelectContent(mod.id, sub.id, ci.id)}
                                                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 4, pr: 2, py: 1, cursor: 'pointer', borderLeft: isCiActive ? `3px solid ${ACCENT}` : '3px solid transparent', bgcolor: isCiActive ? ACCENT_BG : 'transparent', '&:hover': { bgcolor: isCiActive ? ACCENT_BG : '#f8fafc' }, transition: 'all 0.15s' }}>
                                                                                    <Box sx={{ width: 22, height: 22, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontFamily, fontWeight: 700, bgcolor: isCiComplete ? '#10b981' : isCiActive ? ACCENT_BG2 : '#f1f5f9', color: isCiComplete ? 'white' : isCiActive ? ACCENT : '#94a3b8' }}>
                                                                                        {isCiComplete ? <CheckIcon sx={{ fontSize: 12 }} /> : `C${ciIdx + 1}`}
                                                                                    </Box>
                                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: isCiActive ? ACCENT : '#94a3b8' }}>Content {ciIdx + 1}</Typography>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: isCiActive ? 600 : 400, color: isCiActive ? ACCENT_DARK : '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ci.title}</Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        })}
                                                        {mod.has_test && (
                                                            <Box sx={{ borderTop: '1px solid #f1f5f9', p: 1 }}>
                                                                <Button fullWidth onClick={() => isAllModuleContentCompleted(mod.id) && handleStartTest(mod.id)} disabled={!isAllModuleContentCompleted(mod.id)}
                                                                    startIcon={<GpsFixedIcon sx={{ fontSize: 16 }} />} endIcon={!isAllModuleContentCompleted(mod.id) ? <LockIcon sx={{ fontSize: 12 }} /> : null}
                                                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', fontSize: '0.8rem', bgcolor: isAllModuleContentCompleted(mod.id) ? '#fffbeb' : '#f1f5f9', color: isAllModuleContentCompleted(mod.id) ? '#b45309' : '#94a3b8', '&:hover': { bgcolor: isAllModuleContentCompleted(mod.id) ? '#fef3c7' : '#f1f5f9' }, justifyContent: 'flex-start' }}>
                                                                    Module Quiz
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Paper>
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                {/* Main Content Area */}
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', bgcolor: 'white' }}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {viewMode === 'course-complete' && !isReviewMode && (
                            <CourseCompleteView course={course} onBackToCourses={() => navigate('/student-portal/my-courses')} onReviewContent={handleStartReview} />
                        )}

                        {viewMode === 'test' && activeTestModuleId && currentModuleTest && (
                            <KnowledgeTestView moduleId={activeTestModuleId} test={currentModuleTest} onComplete={handleTestComplete}
                                onBack={() => { setViewMode('content'); setActiveTestModuleId(null); }}
                                onNextModule={handleNextModuleFromTest} isLastModule={isLastModule(activeTestModuleId)}
                                courseTitle={course.title} moduleTitle={currentModuleData?.title} showSnackbar={showSnackbar} />
                        )}

                        {(viewMode === 'content' || isReviewMode) && currentContent && (
                            <ContentDisplay content={currentContent} onComplete={handleMarkComplete} onNext={handleNext} onPrevious={handlePrevious}
                                hasNext={hasNext} hasPrevious={hasPrevious} currentIndex={currentNavIndex} totalItems={totalContentCount}
                                isCompleted={completedContents.has(currentContent.id) || currentContent.is_completed}
                                isCourseCompleted={course.enrollment?.status === 'completed'}
                                isReviewMode={isReviewMode} onBackToComplete={handleBackToComplete}
                                courseTitle={course.title} moduleTitle={currentModuleData?.title || ''} subModuleTitle={currentSubmoduleData?.title || ''} />
                        )}

                        {viewMode === 'content' && !currentContent && course.enrollment?.status !== 'completed' && !isReviewMode && (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Box sx={{ width: 56, height: 56, borderRadius: '16px', background: `linear-gradient(135deg, ${ACCENT_BG}, ${ACCENT_BG2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                    <PlayArrowIcon sx={{ fontSize: 28, color: ACCENT }} />
                                </Box>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 1 }}>Ready to Learn?</Typography>
                                <Typography sx={{ fontFamily, color: '#64748b', mb: 4, textAlign: 'center', maxWidth: 360, fontSize: '0.9rem' }}>Select a lesson from the sidebar to start learning.</Typography>
                                {course.modules?.[0]?.submodules?.[0]?.content_items?.[0] && (
                                    <Button variant="contained" startIcon={<PlayArrowIcon />}
                                        onClick={() => {
                                            const m = course.modules[0], s = m.submodules[0], c = s.content_items[0];
                                            handleSelectContent(m.id, s.id, c.id);
                                        }}
                                        sx={{ fontFamily, fontWeight: 700, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, borderRadius: '12px', px: 4, py: 1.2, boxShadow: `0 8px 24px ${ACCENT}33` }}>
                                        Start Learning
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Chatbot Trigger */}
                {viewMode !== 'course-complete' && !isChatbotOpen && (
                    <Box onClick={() => setIsChatbotOpen(true)}
                        sx={{ position: 'fixed', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 40, width: 48, height: 96, background: `linear-gradient(180deg, ${ACCENT}, ${ACCENT_DARK})`, borderRadius: '16px 0 0 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, cursor: 'pointer', boxShadow: `-4px 0 16px ${ACCENT}44`, '&:hover': { width: 52, boxShadow: `-8px 0 24px ${ACCENT}55` }, transition: 'all 0.3s' }}>
                        <NavigateBeforeIcon sx={{ color: 'white', fontSize: 20 }} />
                        <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SmartToyIcon sx={{ color: 'white', fontSize: 16 }} />
                        </Box>
                        <Typography sx={{ fontFamily, fontSize: '0.5rem', fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: 1 }}>Chat</Typography>
                    </Box>
                )}

                {/* Chatbot Panel */}
                {viewMode !== 'course-complete' && (
                    <ChatbotPanel isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} courseIdentity={courseIdentity} courseTitle={course.title} chatbotId={course.chatbot_id} />
                )}
            </Box>

            {/* AI Instructor Overlay */}
            {instructorActive && (
                <InstructorPresentationView
                    instructorName="AI Instructor"
                    courseTitle={course.title}
                    scriptText={instructorScriptText}
                    scriptType={instructorScriptType}
                    isLoading={instructorLoading}
                    onSkip={handleSkipInstructor}
                />
            )}

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ fontFamily, borderRadius: '12px' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Purchase Modal */}
            <CoursePurchaseModal open={purchaseModalOpen} onClose={() => setPurchaseModalOpen(false)} course={course} onPurchaseSuccess={handlePurchaseSuccess} />
        </Box>
    );
}